/**
 * Leads Management Page
 * Kanban (default) and Table view, dynamic statuses from API
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRestorableState, useScrollMemory } from '../hooks/useListMemory';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { CurrencyInput } from '../components/ui/CurrencyInput';
import { SearchInput } from '../components/ui/SearchInput';
import { leadMatchesSearch } from '../lib/lead-search';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { FilterPopover } from '../components/ui/FilterPopover';
import { SegmentToggle } from '../components/ui/SegmentToggle';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Search, UserPlus, Filter, Edit, Trash2, Eye, X, LayoutGrid, Settings2, Plus, Trophy, XCircle, Calendar, User, ChevronLeft, ChevronRight, Upload, Hash, Phone, Mail, Building2, SlidersHorizontal, EllipsisVertical } from 'lucide-react';
import { useApp } from '../App';
import { Tooltip } from '../UI/Tooltip';
import { useAppSelector } from '../store/hooks';
import { selectHasPermission } from '../store/slices/authSlice';
import { periodRange } from '../lib/period-ranges';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { PageLayout } from '../components/layout/PageLayout';

import { ConfirmModal } from '../components/ui/ConfirmModal';
import { marketingAPI, Lead, UpdateLeadRequest, LeadStatusOption, LeadStatusGroup, LeadTypeOption, Domain, Region, Contact, Customer, Organization, Plant, Series, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, ReportScopeResponse, leadDisplayName, leadDisplayCompany, leadDisplayEmail, leadDisplayPhone, LeadActivityAttachment } from '../lib/marketing-api';
import { NAME_PREFIXES, COUNTRY_CODES, DEFAULT_COUNTRY_CODE, getCountryCodeSearchText, DEFAULT_LEAD_SERIES_STORAGE_KEY } from '../constants';
import { serializeNameWithPrefix, serializePhoneWithCountryCode } from '../lib/name-phone-utils';
import { Modal } from '../components/ui/Modal';
import { getStoredMarketingScope } from '../lib/marketing-scope';



const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  new: { bg: 'bg-blue-100/50', text: 'text-blue-700' },
  contacted: { bg: 'bg-amber-100/50', text: 'text-amber-700' },
  qualified: { bg: 'bg-emerald-100/50', text: 'text-emerald-700' },
  proposal: { bg: 'bg-blue-100/50', text: 'text-blue-700' },
  negotiation: { bg: 'bg-purple-100/50', text: 'text-purple-700' },
  won: { bg: 'bg-green-100/50', text: 'text-green-700' },
  lost: { bg: 'bg-rose-100/50', text: 'text-rose-700' },
};
const DEFAULT_STATUS_COLOR = { bg: 'bg-slate-100/50', text: 'text-slate-700' };

/** Get initials from employee name (e.g. "John Doe" -> "JD"). */
function getInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Return white or black for text on the given hex background. */
function getContrastColor(hex: string): string {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return '#111';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5 ? '#fff' : '#111';
}

/** Today's date as a YYYY-MM-DD string, for date input defaults/limits. */
function getTodayDateInputValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Combine a YYYY-MM-DD date with the current time-of-day into an ISO datetime string. */
function combineDateWithCurrentTime(dateStr: string): string {
  const now = new Date();
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds()).toISOString();
}


export const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useApp();
  const canView = useAppSelector(selectHasPermission('marketing.view_lead'));
  const canCreate = useAppSelector(selectHasPermission('marketing.create_lead'));
  const canEdit = useAppSelector(selectHasPermission('marketing.edit_lead'));
  const canDelete = useAppSelector(selectHasPermission('marketing.delete_lead'));
  const canManageNumberSeries = useAppSelector(selectHasPermission('marketing.admin'));
  const canViewOwner = useAppSelector(selectHasPermission('marketing.admin'));

  const [leadStatuses, setLeadStatuses] = useState<LeadStatusOption[]>([]);
  // Deep-link support: /leads?status_id=5 scrolls to and briefly highlights that Kanban column
  // (e.g. clicked from a dashboard chart) — the board itself stays unfiltered, since hiding
  // every other column would be an unusual interaction for a Kanban.
  const [highlightedStatusId, setHighlightedStatusId] = useState<number | null>(null);
  const columnRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useRestorableState('leads:page', 1);
  const [pageSize, setPageSize] = useRestorableState('leads:pageSize', DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  useScrollMemory('leads', !isLoading);
  const [searchTerm, setSearchTerm] = useRestorableState('leads:searchTerm', '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useRestorableState('leads:debouncedSearchTerm', '');
  const [selectedStatus, setSelectedStatus] = useRestorableState<string>('leads:selectedStatus', 'all');
  const [hideEmptyColumns, setHideEmptyColumns] = useRestorableState('leads:hideEmptyColumns', false);
  const [tempSelectedStatus, setTempSelectedStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [deleteLeadId, setDeleteLeadId] = useState<number | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<LeadStatusOption | null>(null);
  const [statusForm, setStatusForm] = useState({ code: '', label: '', display_order: 0, group_id: undefined as number | undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false });
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleteStatusId, setDeleteStatusId] = useState<number | null>(null);
  const [leadStatusGroups, setLeadStatusGroups] = useState<LeadStatusGroup[]>([]);
  const [editingGroup, setEditingGroup] = useState<LeadStatusGroup | null>(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({ code: '', label: '', expected_duration_days: undefined as number | undefined, follow_up_interval_days: undefined as number | undefined, display_order: 0, is_active: true, hex_color: '' as string });
  const [savingGroup, setSavingGroup] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | undefined>({ key: 'created_at', direction: 'desc' });
  const [deleteGroupId, setDeleteGroupId] = useState<number | null>(null);
  const [draggedLeadId, setDraggedLeadId] = useState<number | null>(null);
  const [dragOverStatusId, setDragOverStatusId] = useState<number | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<number | null>(null);
  const [viewingAttachmentLeadId, setViewingAttachmentLeadId] = useState<number | null>(null);
  const [showWonClosedValueModal, setShowWonClosedValueModal] = useState(false);
  const [pendingWonLeadId, setPendingWonLeadId] = useState<number | null>(null);
  const [pendingWonStatusId, setPendingWonStatusId] = useState<number | null>(null);
  const [closedValueInput, setClosedValueInput] = useState('');
  const [wonPoFile, setWonPoFile] = useState<File | null>(null);
  const [wonDateInput, setWonDateInput] = useState('');
  const [leadToMarkLost, setLeadToMarkLost] = useState<number | null>(null);
  const [markLostReason, setMarkLostReason] = useState('');
  const [markLostCompetitor, setMarkLostCompetitor] = useState('');
  const [markLostPrice, setMarkLostPrice] = useState('');
  const [markLostSubmitting, setMarkLostSubmitting] = useState(false);
  /** When user drags lead to another status: show enquiry popup to fill reason, then update status */
  const [statusChangePending, setStatusChangePending] = useState<{ leadId: number; currentStatusId: number | undefined; newStatusId: number } | null>(null);
  const [statusChangeForm, setStatusChangeForm] = useState({ title: '', description: '' });
  const [statusChangeAttachments, setStatusChangeAttachments] = useState<{ id: string; kind: 'new-quotation' | 'revise-quotation' | 'attachment'; file: File | null; title: string; quoteValue: string; reviseTargetQuotation: string }[]>([{ id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }]);
  const [statusChangeSubmitting, setStatusChangeSubmitting] = useState(false);
  const [statusChangeUploadProgress, setStatusChangeUploadProgress] = useState<number | null>(null);
  const [statusChangeBaseQuotations, setStatusChangeBaseQuotations] = useState<string[]>([]);
  const [statusChangeActivitiesLoading, setStatusChangeActivitiesLoading] = useState(false);
  const [wonPoUploadProgress, setWonPoUploadProgress] = useState<number | null>(null);
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [statusChangeSeriesCode, setStatusChangeSeriesCode] = useState('');
  const [statusChangeGeneratingQuote, setStatusChangeGeneratingQuote] = useState(false);
  const [showCreateLeadModal, setShowCreateLeadModal] = useState(false);
  const [createLeadModalStatusId, setCreateLeadModalStatusId] = useState<number | null>(null);
  const [createLeadSearchName, setCreateLeadSearchName] = useState('');
  const [createLeadContactResults, setCreateLeadContactResults] = useState<Contact[]>([]);
  const [createLeadCustomerResults, setCreateLeadCustomerResults] = useState<Customer[]>([]);
  const [createLeadContactId, setCreateLeadContactId] = useState<number | undefined>(undefined);
  const [createLeadCustomerId, setCreateLeadCustomerId] = useState<number | undefined>(undefined);
  const [createLeadInlineContact, setCreateLeadInlineContact] = useState({
    domain_id: undefined as number | undefined,
    region_id: undefined as number | undefined,
    organization_id: undefined as number | undefined,
    plant_id: undefined as number | undefined,
    title: '',
    first_name: '',
    last_name: '',
    contact_email: '',
    contact_phone_code: DEFAULT_COUNTRY_CODE,
    contact_phone: '',
  });
  const [createLeadInlineOrgQuery, setCreateLeadInlineOrgQuery] = useState('');
  const [createLeadInlineOrgSuggestions, setCreateLeadInlineOrgSuggestions] = useState<Organization[]>([]);
  const [createLeadInlinePlants, setCreateLeadInlinePlants] = useState<Plant[]>([]);
  const createLeadSearchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const createLeadOrgTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [createLeadForm, setCreateLeadForm] = useState({
    domain_id: undefined as number | undefined,
    region_id: undefined as number | undefined,
    status_id: undefined as number | undefined,
    lead_type_id: undefined as number | undefined,
    notes: '',
  });
  const [createLeadDomains, setCreateLeadDomains] = useState<Domain[]>([]);
  const [createLeadRegions, setCreateLeadRegions] = useState<Region[]>([]);
  const [createLeadSubmitting, setCreateLeadSubmitting] = useState(false);
  const [showNumberSeriesModal, setShowNumberSeriesModal] = useState(false);
  const [defaultLeadSeriesCode, setDefaultLeadSeriesCode] = useState('');
  const canCreateContact = useAppSelector(selectHasPermission('marketing.create_contact'));
  const canCreateOrg = useAppSelector(selectHasPermission('marketing.create_organization'));
  const [leadTypes, setLeadTypes] = useState<LeadTypeOption[]>([]);
  const [showLeadTypeModal, setShowLeadTypeModal] = useState(false);
  const [editingLeadType, setEditingLeadType] = useState<LeadTypeOption | null>(null);
  const [leadTypeForm, setLeadTypeForm] = useState({ code: '', label: '', display_order: 0, is_active: true });
  const [savingLeadType, setSavingLeadType] = useState(false);
  const [deleteLeadTypeId, setDeleteLeadTypeId] = useState<number | null>(null);
  const [isScopeLoading, setIsScopeLoading] = useState(true);
  const [reportScope, setReportScope] = useState<ReportScopeResponse | null>(() => {
    const cached = getStoredMarketingScope();
    if (!cached) return null;
    return {
      can_select_employee: cached.role !== 'self' && cached.role !== 'employee' && cached.role !== 'supervisor',
      employees: [],
      role: cached.role === 'supervisor' ? 'self' : cached.role === 'employee' ? 'self' : (cached.role as any),
      is_domain_coordinator: cached.is_domain_coordinator,
    };
  });
  const [dateFromInput, setDateFromInput] = useRestorableState('leads:dateFromInput', '');
  const [dateToInput, setDateToInput] = useRestorableState('leads:dateToInput', '');
  const [appliedDateFrom, setAppliedDateFrom] = useRestorableState('leads:appliedDateFrom', '');
  const [appliedDateTo, setAppliedDateTo] = useRestorableState('leads:appliedDateTo', '');
  const [dateRangePreset, setDateRangePreset] = useRestorableState<string>('leads:dateRangePreset', 'all');

  const getPresetDates = (preset: string): { from: string; to: string } => {
    const now = new Date();
    let fromDate: Date | null = null;
    let toDate: Date | null = null;

    switch (preset) {
      case 'today':
        fromDate = now;
        toDate = now;
        break;
      case 'yesterday':
        fromDate = subDays(now, 1);
        toDate = subDays(now, 1);
        break;
      case 'this_week':
        fromDate = startOfWeek(now, { weekStartsOn: 1 });
        toDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'this_month':
        fromDate = startOfMonth(now);
        toDate = endOfMonth(now);
        break;
      case 'this_quarter': {
        // Financial-year quarter (Q1 = Apr–Jun), same as the Domains targets
        const q = periodRange('quarter', now);
        return { from: q.date_from!, to: q.date_to! };
      }
      case 'last_30_days':
        fromDate = subDays(now, 30);
        toDate = now;
        break;
      default:
        return { from: '', to: '' };
    }

    return {
      from: format(fromDate, 'yyyy-MM-dd'),
      to: format(toDate, 'yyyy-MM-dd'),
    };
  };

  const handlePresetChange = (preset: string) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      const { from, to } = getPresetDates(preset);
      setAppliedDateFrom(from);
      setAppliedDateTo(to);
      setDateFromInput(from);
      setDateToInput(to);
    }
  };
  const [selectedAssignedToIds, setSelectedAssignedToIds] = useRestorableState<number[]>('leads:selectedAssignedToIds', []);
  const [createdByMeOnly, setCreatedByMeOnly] = useRestorableState('leads:createdByMeOnly', false);
  const [includeWonLost, setIncludeWonLost] = useRestorableState('leads:includeWonLost', false);
  const [showEmployeeFilterPopover, setShowEmployeeFilterPopover] = useState(false);
  const filterButtonRef = React.useRef<HTMLDivElement>(null);
  const employeeFilterRef = React.useRef<HTMLDivElement>(null);
  const didDragRef = React.useRef(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Auto-scroll while dragging a lead card near the edge of a kanban scroll container (board row or a group's status row).
  const autoScrollTargetRef = React.useRef<{ el: HTMLDivElement; velocity: number } | null>(null);
  const autoScrollRafRef = React.useRef<number | null>(null);

  const isHeadOrAdmin = reportScope?.role !== 'self' && reportScope?.role !== undefined;

  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!canView) {
      showToast('You do not have permission to view leads (marketing.lead.view)', 'error');
      return;
    }
    marketingAPI.getLeadStatuses().then(setLeadStatuses).catch(() => setLeadStatuses([]));
    marketingAPI.getLeadStatusGroups().then(setLeadStatusGroups).catch(() => setLeadStatusGroups([]));
    marketingAPI.getLeadTypes().then(setLeadTypes).catch(() => setLeadTypes([]));
    
    setIsScopeLoading(true);
    marketingAPI.getReportsScope()
      .then((scope) => {
        setReportScope(scope);
      })
      .catch(() => {
        setReportScope(null);
      })
      .finally(() => {
        setIsScopeLoading(false);
      });

    marketingAPI.getSeries({ page: 1, page_size: 100, is_active: true }).then((r) => setSeriesList(r.items ?? [])).catch(() => setSeriesList([]));
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    loadLeads();
  // Search is done on screen (lib/lead-search.ts) — the API ignores `search`, so typing doesn't refetch.
  }, [canView, appliedDateFrom, appliedDateTo, selectedAssignedToIds, createdByMeOnly, includeWonLost]);

  useEffect(() => {
    if (includeWonLost) return;
    const wonLostIds = new Set(
      leadStatuses.filter((s) => (s.is_final ?? false) || (s.is_lost ?? false)).map((s) => s.id)
    );
    if (selectedStatus !== 'all' && wonLostIds.has(parseInt(selectedStatus, 10))) {
      setSelectedStatus('all');
      setTempSelectedStatus('all');
    }
  }, [includeWonLost, leadStatuses, selectedStatus]);

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      const res = await marketingAPI.getLeads({
        page: 1,
        no_limit: true,
        date_from: isHeadOrAdmin && appliedDateFrom ? appliedDateFrom : undefined,
        date_to: isHeadOrAdmin && appliedDateTo ? appliedDateTo : undefined,
        assigned_to: selectedAssignedToIds.length > 0 ? selectedAssignedToIds : undefined,
        created_by_me: createdByMeOnly || undefined,
        include_won_lost: includeWonLost || undefined,
      });
      setLeads(res.items);
      setTotal(res.total);
      setTotalPages(res.total_pages);
    } catch (error) {
      showToast('Failed to load leads', 'error');
      console.error('Error loading leads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const filteredLeads = useMemo(
    () => (debouncedSearchTerm.trim() ? leads.filter((lead) => leadMatchesSearch(lead, debouncedSearchTerm)) : leads),
    [leads, debouncedSearchTerm]
  );

  const leadsByStatus = useMemo(() => {
    const map: Record<string, Lead[]> = {};
    leadStatuses.forEach((s) => { map[s.code] = []; });
    filteredLeads.forEach((lead) => {
      const code = lead.status_option?.code ?? leadStatuses.find((s) => s.id === lead.status_id)?.code ?? lead.status ?? 'new';
      if (!map[code]) map[code] = [];
      map[code].push(lead);
    });
    // Sort each status column so latest leads appear at the top
    Object.keys(map).forEach((code) => {
      map[code].sort((a, b) => {
        const timeA = new Date(a.updated_at || a.last_activity_date || a.created_at || 0).getTime() || a.id;
        const timeB = new Date(b.updated_at || b.last_activity_date || b.created_at || 0).getTime() || b.id;
        return timeB - timeA;
      });
    });
    return map;
  }, [filteredLeads, leadStatuses]);

  /** Board: groups in order; Won/Lost block only shown when includeWonLost is on; otherwise that status group is hidden too */
  const statusGroupsForBoard = useMemo(() => {
    const activeStatuses = leadStatuses.filter((s) => s.is_active);
    const wonLostStatuses = activeStatuses.filter((s) => (s.is_final ?? false) || (s.is_lost ?? false));
    const wonLostIds = new Set(wonLostStatuses.map((s) => s.id));
    const order = [...leadStatusGroups.filter((g) => g.is_active).map((g) => g.id), 'none' as const];
    const mainBlocks = order.map((groupId): { groupId: number | 'none' | 'closed'; groupLabel: string; statuses: LeadStatusOption[] } => {
      const statuses = activeStatuses.filter(
        (s) => (s.group_id ?? null) === (groupId === 'none' ? null : groupId) && !wonLostIds.has(s.id)
      );
      const groupLabel = groupId === 'none' ? '— No group —' : leadStatusGroups.find((g) => g.id === groupId)?.label ?? `Group #${groupId}`;
      return { groupId, groupLabel, statuses };
    }).filter((block) => block.statuses.length > 0);
    if (includeWonLost && wonLostStatuses.length > 0) {
      mainBlocks.push({ groupId: 'closed' as const, groupLabel: 'Won / Lost', statuses: wonLostStatuses });
    }
    return mainBlocks;
  }, [leadStatuses, leadStatusGroups, includeWonLost]);

  const openDeleteConfirm = (id: number) => {
    if (!canDelete) {
      showToast('You do not have permission to delete leads', 'error');
      return;
    }
    setDeleteLeadId(id);
  };

  /** Opens the most recently uploaded quotation attachment for a lead, fetched on demand (not pre-loaded per card). */
  const handleViewLatestAttachment = async (lead: Lead) => {
    setViewingAttachmentLeadId(lead.id);
    try {
      const activities = await marketingAPI.getLeadActivities(lead.id);
      const files: LeadActivityAttachment[] = [];
      for (const act of activities) {
        for (const att of (act.attachments || [])) files.push(att);
      }
      if (files.length === 0) {
        showToast('No attachment found for this lead', 'error');
        return;
      }
      const latest = files[files.length - 1];
      const url = await marketingAPI.getLeadActivityAttachmentUrl(lead.id, latest.activity_id, latest.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      showToast(err?.message || 'Failed to open attachment', 'error');
    } finally {
      setViewingAttachmentLeadId(null);
    }
  };

  const handleConfirmDeleteLead = async () => {
    if (deleteLeadId == null) return;
    try {
      await marketingAPI.deleteLead(deleteLeadId);
      showToast('Lead deleted successfully', 'success');
      loadLeads();
    } catch (error) {
      showToast('Failed to delete lead', 'error');
    }
  };

  const breadcrumbs = [
    { label: 'Leads' },
  ];

  /** Sooner of the manually-scheduled follow-up and the server-computed auto follow-up (status-group interval). */
  const getEffectiveFollowUpDate = (lead: Lead): string | null => {
    const manual = lead.next_follow_up_at;
    const auto = lead.auto_follow_up_at;
    if (manual && auto) return new Date(manual) <= new Date(auto) ? manual : auto;
    return manual || auto || null;
  };

  /** How urgent a lead's next follow-up is, for graded Kanban card highlighting */
  const getFollowUpUrgency = (lead: Lead): 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | null => {
    const effective = getEffectiveFollowUpDate(lead);
    if (!effective) return null;
    const due = new Date(effective);
    const now = new Date();
    if (due <= now) return 'overdue';
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const dayDiff = Math.round((startOfDueDay.getTime() - startOfToday.getTime()) / 86400000);
    if (dayDiff === 0) return 'today';
    if (dayDiff === 1) return 'tomorrow';
    if (dayDiff <= 7) return 'week';
    return 'later';
  };

  // leadColumns removed — table view has been removed; Kanban is the only view.

  const openStatusModal = () => {
    setEditingStatus(null);
    setEditingGroup(null);
    setStatusForm({ code: '', label: '', display_order: leadStatuses.length, group_id: undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false });
    setGroupForm({ code: '', label: '', expected_duration_days: undefined, follow_up_interval_days: undefined, display_order: leadStatusGroups.length, is_active: true, hex_color: '' });
    setShowStatusModal(true);
    marketingAPI.getLeadStatusGroups().then(setLeadStatusGroups).catch(() => setLeadStatusGroups([]));
  };
  const openEditStatus = (s: LeadStatusOption) => {
    setEditingStatus(s);
    setStatusForm({ code: s.code, label: s.label, display_order: s.display_order, group_id: s.group_id ?? undefined, is_active: s.is_active, is_final: s.is_final ?? false, is_lost: s.is_lost ?? false, hex_color: s.hex_color ?? '', set_when_quotation_added: s.set_when_quotation_added ?? false, set_when_quote_number_generated: s.set_when_quote_number_generated ?? false, attachment_required_on_kanban_change: s.attachment_required_on_kanban_change ?? false, is_hot: s.is_hot ?? false });
    setShowStatusModal(true);
    marketingAPI.getLeadStatusGroups().then(setLeadStatusGroups).catch(() => setLeadStatusGroups([]));
  };
  /** Add a new status under a specific group (create groups first, then add statuses to each group). */
  const openAddStatusToGroup = (groupId: number) => {
    setEditingStatus(null);
    const group = leadStatusGroups.find((g) => g.id === groupId);
    const statusesInGroup = leadStatuses.filter((s) => s.group_id === groupId);
    setStatusForm({
      code: '',
      label: '',
      display_order: statusesInGroup.length,
      group_id: groupId,
      is_active: true,
      is_final: false,
      is_lost: false,
      hex_color: '',
      set_when_quotation_added: false,
      set_when_quote_number_generated: false,
      attachment_required_on_kanban_change: false,
      is_hot: false,
    });
    setShowStatusModal(true);
    marketingAPI.getLeadStatusGroups().then(setLeadStatusGroups).catch(() => setLeadStatusGroups([]));
  };
  const saveGroup = async () => {
    if (!groupForm.label.trim()) {
      showToast('Group label is required', 'error');
      return;
    }
    const code = (groupForm.code || groupForm.label).trim().toLowerCase().replace(/\s+/g, '_');
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await marketingAPI.updateLeadStatusGroup(editingGroup.id, {
          code,
          label: groupForm.label.trim(),
          expected_duration_days: groupForm.expected_duration_days ?? undefined,
          follow_up_interval_days: groupForm.follow_up_interval_days ?? undefined,
          display_order: groupForm.display_order,
          is_active: groupForm.is_active,
          hex_color: groupForm.hex_color?.trim() || undefined,
        });
        showToast('Group updated', 'success');
      } else {
        await marketingAPI.createLeadStatusGroup({
          code,
          label: groupForm.label.trim(),
          expected_duration_days: groupForm.expected_duration_days ?? undefined,
          follow_up_interval_days: groupForm.follow_up_interval_days ?? undefined,
          display_order: groupForm.display_order,
          is_active: groupForm.is_active,
          hex_color: groupForm.hex_color?.trim() || undefined,
        });
        showToast('Group created', 'success');
      }
      const list = await marketingAPI.getLeadStatusGroups();
      setLeadStatusGroups(list);
      setEditingGroup(null);
      setAddingGroup(false);
      setGroupForm({ code: '', label: '', expected_duration_days: undefined, follow_up_interval_days: undefined, display_order: list.length, is_active: true, hex_color: '' });
    } catch (e: any) {
      showToast(e.message || 'Failed to save group', 'error');
    } finally {
      setSavingGroup(false);
    }
  };
  const cancelGroupForm = () => {
    setAddingGroup(false);
    setEditingGroup(null);
    setGroupForm({ code: '', label: '', expected_duration_days: undefined, follow_up_interval_days: undefined, display_order: leadStatusGroups.length, is_active: true, hex_color: '' });
  };
  const confirmDeleteGroup = async () => {
    if (deleteGroupId == null) return;
    try {
      await marketingAPI.deleteLeadStatusGroup(deleteGroupId);
      showToast('Group deleted', 'success');
      const list = await marketingAPI.getLeadStatusGroups();
      setLeadStatusGroups(list);
      setDeleteGroupId(null);
    } catch (e: any) {
      showToast(e.message || 'Failed to delete group', 'error');
    }
  };
  const saveStatus = async () => {
    if (!statusForm.label.trim()) {
      showToast('Label is required', 'error');
      return;
    }
    const code = (statusForm.code || statusForm.label).trim().toLowerCase().replace(/\s+/g, '_');
    if (!code) {
      showToast('Code is required', 'error');
      return;
    }
    setSavingStatus(true);
    try {
      if (editingStatus) {
        await marketingAPI.updateLeadStatus(editingStatus.id, {
          code,
          label: statusForm.label.trim(),
          display_order: statusForm.display_order,
          is_active: statusForm.is_active,
          is_final: statusForm.is_final,
          is_lost: statusForm.is_lost,
          hex_color: statusForm.hex_color?.trim() ? statusForm.hex_color.trim() : undefined,
          set_when_quotation_added: statusForm.set_when_quotation_added,
          set_when_quote_number_generated: statusForm.set_when_quote_number_generated,
          attachment_required_on_kanban_change: statusForm.attachment_required_on_kanban_change,
          is_hot: statusForm.is_hot,
        });
        showToast('Status updated', 'success');
      } else {
        await marketingAPI.createLeadStatus({
          code,
          label: statusForm.label.trim(),
          group_id: statusForm.group_id ?? undefined,
          display_order: statusForm.display_order,
          is_active: statusForm.is_active,
          is_final: statusForm.is_final,
          is_lost: statusForm.is_lost,
          hex_color: statusForm.hex_color?.trim() ? statusForm.hex_color.trim() : undefined,
          set_when_quotation_added: statusForm.set_when_quotation_added,
          set_when_quote_number_generated: statusForm.set_when_quote_number_generated,
          attachment_required_on_kanban_change: statusForm.attachment_required_on_kanban_change,
          is_hot: statusForm.is_hot,
        });
        showToast('Status created', 'success');
      }
      const list = await marketingAPI.getLeadStatuses();
      setLeadStatuses(list);
      setEditingStatus(null);
      setStatusForm({ code: '', label: '', display_order: list.length, group_id: undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false });
    } catch (e: any) {
      showToast(e.message || 'Failed to save status', 'error');
    } finally {
      setSavingStatus(false);
    }
  };
  /** True if lead is in Won (final) or Lost status – these leads are not draggable. */
  const isWonOrLostLead = (lead: Lead): boolean => {
    const opt = lead.status_option;
    if (!opt) return false;
    return !!(opt.is_final || opt.is_lost);
  };

  const handleLeadDragStart = (e: React.DragEvent, lead: Lead) => {
    if (!canEdit || isWonOrLostLead(lead)) {
      e.preventDefault();
      return;
    }
    didDragRef.current = false;
    e.dataTransfer.setData('application/json', JSON.stringify({ leadId: lead.id, currentStatusId: lead.status_id }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedLeadId(lead.id);
  };

  const runAutoScroll = () => {
    const target = autoScrollTargetRef.current;
    if (!target || target.velocity === 0) {
      autoScrollRafRef.current = null;
      return;
    }
    target.el.scrollLeft += target.velocity;
    autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
  };

  /** Scrolls a kanban scroll container (board row or a group's status row) when a dragged card nears its left/right edge. */
  const handleBoardDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const EDGE = 80; // px from the edge that starts auto-scrolling
    const MAX_SPEED = 16; // px per animation frame at the very edge
    let velocity = 0;
    if (e.clientX < rect.left + EDGE) {
      velocity = -MAX_SPEED * (1 - Math.max(0, e.clientX - rect.left) / EDGE);
    } else if (e.clientX > rect.right - EDGE) {
      velocity = MAX_SPEED * (1 - Math.max(0, rect.right - e.clientX) / EDGE);
    }
    if (velocity === 0) {
      if (autoScrollTargetRef.current?.el === container) autoScrollTargetRef.current = null;
      return;
    }
    autoScrollTargetRef.current = { el: container, velocity };
    if (autoScrollRafRef.current == null) {
      autoScrollRafRef.current = requestAnimationFrame(runAutoScroll);
    }
  };

  const stopAutoScroll = () => {
    autoScrollTargetRef.current = null;
    if (autoScrollRafRef.current != null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  };

  const handleLeadDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverStatusId(null);
    stopAutoScroll();
    setTimeout(() => { didDragRef.current = false; }, 0);
  };
  const handleColumnDragOver = (e: React.DragEvent, statusId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatusId(statusId);
  };
  const handleColumnDragLeave = () => {
    setDragOverStatusId(null);
  };
  const newStatusIsWon = (statusId: number) => {
    const s = leadStatuses.find((x) => x.id === statusId);
    return s && s.is_final && !s.is_lost;
  };

  const handleColumnDrop = async (e: React.DragEvent, newStatusId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverStatusId(null);
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    let leadId: number;
    let currentStatusId: number;
    try {
      ({ leadId, currentStatusId } = JSON.parse(raw));
    } catch {
      return;
    }
    // Do not allow moving leads that are already Won or Lost
    const fromStatus = leadStatuses.find((s) => s.id === currentStatusId);
    if (fromStatus && ((fromStatus.is_final ?? false) || (fromStatus.is_lost ?? false))) {
      setDraggedLeadId(null);
      return;
    }
    didDragRef.current = true;
    if (currentStatusId === newStatusId) return;
    if (newStatusIsWon(newStatusId)) {
      setPendingWonLeadId(leadId);
      setPendingWonStatusId(newStatusId);
      setClosedValueInput('');
      setWonPoFile(null);
      setWonDateInput(getTodayDateInputValue());
      setShowWonClosedValueModal(true);
      setDraggedLeadId(null);
      return;
    }
    setStatusChangePending({ leadId, currentStatusId, newStatusId });
    setStatusChangeForm({ title: '', description: '' });
    setStatusChangeAttachments([{ id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }]);
    setStatusChangeSeriesCode('');
    setStatusChangeBaseQuotations([]);
    setDraggedLeadId(null);
    setStatusChangeActivitiesLoading(true);
    marketingAPI.getLeadActivities(leadId)
      .then((activities) => {
        const qns = new Set<string>();
        for (const a of activities) {
          for (const att of (a.attachments || [])) {
            if (att.is_quotation && att.quotation_number) {
              const base = att.quotation_number.replace(/\(rev\d+\)\s*$/, '').trim();
              if (base) qns.add(base);
            }
          }
        }
        setStatusChangeBaseQuotations(Array.from(qns));
      })
      .catch(() => { setStatusChangeBaseQuotations([]); })
      .finally(() => { setStatusChangeActivitiesLoading(false); });
  };

  const handleStatusChangeModalSubmit = async () => {
    if (!statusChangePending) return;
    const { leadId, currentStatusId, newStatusId } = statusChangePending;
    const pendingLead = leads.find((l) => l.id === leadId);
    const pendingStatus = leadStatuses.find((s) => s.id === newStatusId);
    if (pendingStatus?.set_when_quote_number_generated && !pendingLead?.series?.trim()) {
      showToast('Generate quotation number before changing to this status', 'error');
      return;
    }
    const enteredTitle = statusChangeForm.title.trim();
    const enteredDescription = statusChangeForm.description?.trim() || '';
    if (!enteredTitle) {
      showToast('Title is required', 'error');
      return;
    }
    if (!enteredDescription) {
      showToast('Description is required', 'error');
      return;
    }
    const requiresAttachment = Boolean(pendingStatus?.attachment_required_on_kanban_change);
    const toUpload = statusChangeAttachments.filter((e) => e.file);
    if (requiresAttachment && toUpload.length === 0) {
      showToast('At least one attachment is required', 'error');
      return;
    }
    setStatusChangeSubmitting(true);
    if (toUpload.length > 0) {
      setStatusChangeUploadProgress(0);
    }
    try {
      const created = await marketingAPI.createLeadActivity(leadId, {
        activity_type: 'lead_status_change',
        title: enteredTitle,
        description: enteredDescription,
        from_status_id: currentStatusId ?? undefined,
        to_status_id: newStatusId,
      });
      if (toUpload.length > 0) {
        const hasRevise = toUpload.some((e) => e.kind === 'revise-quotation');
        await marketingAPI.uploadLeadActivityAttachments(
          leadId,
          created.id,
          toUpload.map((e) => e.file!),
          toUpload.map((e) => (e.kind === 'attachment' ? 'attachment' as const : 'quotation' as const)),
          toUpload.map((e) => (e.kind === 'revise-quotation' ? (e.reviseTargetQuotation || undefined) : undefined)),
          toUpload.map((e) => (e.kind === 'attachment' ? (e.title.trim() || undefined) : undefined)),
          undefined,
          hasRevise || undefined,
          toUpload.map((e) => (e.kind !== 'attachment' && e.quoteValue ? Number(e.quoteValue) : undefined)),
          setStatusChangeUploadProgress
        );
      }
      await marketingAPI.updateLead(leadId, { status_id: newStatusId });
      showToast('Status updated and log saved', 'success');
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, status_id: newStatusId, status_option: leadStatuses.find((s) => s.id === newStatusId) ?? l.status_option } : l
        )
      );
      setStatusChangePending(null);
      setStatusChangeForm({ title: '', description: '' });
      setStatusChangeAttachments([{ id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }]);
      setStatusChangeSeriesCode('');
      setStatusChangeBaseQuotations([]);
    } catch (err: any) {
      showToast(err?.message || 'Failed to update lead status', 'error');
    } finally {
      setStatusChangeSubmitting(false);
      setStatusChangeUploadProgress(null);
    }
  };

  const handleStatusChangeGenerateQuote = async () => {
    if (!statusChangePending || !statusChangeSeriesCode.trim()) return;
    const leadId = statusChangePending.leadId;
    setStatusChangeGeneratingQuote(true);
    try {
      const res = await marketingAPI.generateNextSeriesNumberByCode(statusChangeSeriesCode.trim(), { lead_id: leadId });
      const generated = res.generated_value ?? '';
      await marketingAPI.updateLead(leadId, { series_code: statusChangeSeriesCode.trim(), series: generated } as UpdateLeadRequest);
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, series_code: statusChangeSeriesCode.trim(), series: generated } : l
        )
      );
      showToast('Lead number generated', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to generate lead number', 'error');
    } finally {
      setStatusChangeGeneratingQuote(false);
    }
  };

  const handleWonClosedValueSubmit = async () => {
    if (pendingWonLeadId == null || pendingWonStatusId == null || !closedValueInput.trim()) return;
    const value = parseFloat(closedValueInput.replace(/,/g, '').trim());
    if (Number.isNaN(value) || value < 0) {
      showToast('Please enter a valid positive number for closed value', 'error');
      return;
    }
    if (!wonPoFile) {
      showToast('PO file is required to mark lead as Won', 'error');
      return;
    }
    const isBackdated = canCreate && wonDateInput && wonDateInput !== getTodayDateInputValue();
    if (isBackdated && wonDateInput > getTodayDateInputValue()) {
      showToast('Won date cannot be in the future', 'error');
      return;
    }
    const backdatedIso = isBackdated ? combineDateWithCurrentTime(wonDateInput) : undefined;
    setUpdatingLeadId(pendingWonLeadId);
    setWonPoUploadProgress(0);
    try {
      const fromStatusId = leads.find((l) => l.id === pendingWonLeadId)?.status_id;
      const created = await marketingAPI.createLeadActivity(pendingWonLeadId, {
        activity_type: 'lead_status_change',
        title: 'Lead marked as Won',
        description: `Closed value: ₹${value.toLocaleString()}. PO file attached.${backdatedIso ? ' (Backdated Won date)' : ''}`,
        from_status_id: fromStatusId ?? undefined,
        to_status_id: pendingWonStatusId,
        activity_date: backdatedIso,
      });
      await marketingAPI.uploadLeadActivityAttachments(
        pendingWonLeadId,
        created.id,
        [wonPoFile],
        ['attachment'],
        undefined,
        ['PO File'],
        undefined,
        undefined,
        undefined,
        setWonPoUploadProgress
      );
      await marketingAPI.updateLead(pendingWonLeadId, {
        status_id: pendingWonStatusId,
        closed_value: value,
        ...(backdatedIso ? { closed_at: backdatedIso } : {}),
      } as UpdateLeadRequest);
      showToast(backdatedIso ? 'Lead marked as Won with a backdated Won date' : 'Lead marked as Won with closed value and PO file', 'success');
      setLeads((prev) =>
        prev.map((l) =>
          l.id === pendingWonLeadId
            ? { ...l, status_id: pendingWonStatusId, status_option: leadStatuses.find((s) => s.id === pendingWonStatusId) ?? l.status_option, closed_value: value }
            : l
        )
      );
      setShowWonClosedValueModal(false);
      setPendingWonLeadId(null);
      setPendingWonStatusId(null);
      setClosedValueInput('');
      setWonPoFile(null);
      setWonDateInput('');
      navigate(`/orders/new?lead_id=${pendingWonLeadId}`);
    } catch (err: any) {
      showToast(err.message || 'Failed to update lead', 'error');
    } finally {
      setUpdatingLeadId(null);
      setWonPoUploadProgress(null);
      setShowWonClosedValueModal(false);
      setPendingWonLeadId(null);
      setPendingWonStatusId(null);
      setClosedValueInput('');
      setWonPoFile(null);
      setWonDateInput('');
    }
  };

  const confirmDeleteStatus = async () => {
    if (deleteStatusId == null) return;
    try {
      await marketingAPI.deleteLeadStatus(deleteStatusId);
      showToast('Status deleted', 'success');
      const list = await marketingAPI.getLeadStatuses();
      setLeadStatuses(list);
      setDeleteStatusId(null);
    } catch (e: any) {
      showToast(e.message || 'Failed to delete status', 'error');
    }
  };

  const firstActiveStatusId = leadStatuses.find((s) => s.is_active)?.id;
  const wonStatusId = useMemo(() => leadStatuses.find((s) => s.is_final && !s.is_lost)?.id ?? null, [leadStatuses]);
  const lostStatusId = useMemo(() => leadStatuses.find((s) => s.is_lost)?.id ?? null, [leadStatuses]);
  const pendingToStatus = statusChangePending ? leadStatuses.find((s) => s.id === statusChangePending.newStatusId) : null;
  const pendingStatusRequiresAttachment = Boolean(pendingToStatus?.attachment_required_on_kanban_change);

  const openMarkAsWonModal = (leadId: number) => {
    if (!wonStatusId) {
      showToast('No Won status configured. Add a status marked as Final (Won) in Manage statuses.', 'error');
      return;
    }
    setPendingWonLeadId(leadId);
    setPendingWonStatusId(wonStatusId);
    setClosedValueInput('');
    setWonPoFile(null);
    setWonDateInput(getTodayDateInputValue());
    setShowWonClosedValueModal(true);
  };

  const handleMarkAsLostConfirm = async () => {
    if (leadToMarkLost == null || lostStatusId == null || markLostReason.trim().length < 100) return;
    setMarkLostSubmitting(true);
    setUpdatingLeadId(leadToMarkLost);
    try {
      await marketingAPI.updateLead(leadToMarkLost, {
        status_id: lostStatusId ?? undefined,
        status_change_reason: markLostReason.trim(),
        lost_to_competitor: markLostCompetitor.trim() || 'Not sure',
        lost_at_price: markLostPrice.trim() || 'Not sure',
      } as UpdateLeadRequest);
      showToast('Lead marked as Lost', 'success');
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadToMarkLost
            ? { ...l, status_id: lostStatusId, status_option: leadStatuses.find((s) => s.id === lostStatusId) ?? l.status_option }
            : l
        )
      );
      setLeadToMarkLost(null);
      setMarkLostReason('');
      setMarkLostCompetitor('');
      setMarkLostPrice('');
    } catch (err: any) {
      showToast(err.message || 'Failed to update lead', 'error');
    } finally {
      setUpdatingLeadId(null);
      setMarkLostSubmitting(false);
    }
  };

  const openCreateLeadModal = (statusId?: number) => {
    const sid = statusId ?? firstActiveStatusId ?? undefined;
    const query = sid != null ? `?status_id=${sid}` : '';
    navigate(`/leads/new${query}`);
  };

  const closeCreateLeadModal = () => {
    setShowCreateLeadModal(false);
    setCreateLeadModalStatusId(null);
    setCreateLeadSearchName('');
    setCreateLeadContactResults([]);
    setCreateLeadCustomerResults([]);
    setCreateLeadContactId(undefined);
    setCreateLeadCustomerId(undefined);
    setCreateLeadInlineContact({
      domain_id: undefined,
      region_id: undefined,
      organization_id: undefined,
      plant_id: undefined,
      title: '',
      first_name: '',
      last_name: '',
      contact_email: '',
      contact_phone_code: DEFAULT_COUNTRY_CODE,
      contact_phone: '',
    });
    setCreateLeadInlineOrgQuery('');
    setCreateLeadInlineOrgSuggestions([]);
    setCreateLeadInlinePlants([]);
    setCreateLeadForm({
      domain_id: undefined,
      region_id: undefined,
      status_id: undefined,
      lead_type_id: undefined,
      notes: '',
    });
  };

  useEffect(() => {
    if (!showCreateLeadModal) return;
    Promise.all([
      marketingAPI.getDomains({ page: 1, page_size: 100, is_active: true }),
      marketingAPI.getRegions({ page: 1, page_size: 100, is_active: true }),
    ]).then(([domainsRes, regionsRes]) => {
      setCreateLeadDomains(domainsRes.items);
      setCreateLeadRegions(regionsRes.items);
    }).catch(() => {
      showToast('Failed to load domains/regions', 'error');
    });
  }, [showCreateLeadModal]);

  useEffect(() => {
    if (!showCreateLeadModal || !createLeadInlineContact.domain_id) return;
    marketingAPI.getRegions({ domain_id: createLeadInlineContact.domain_id, is_active: true, page: 1, page_size: 100 })
      .then((res) => setCreateLeadRegions(res.items ?? []))
      .catch(() => setCreateLeadRegions([]));
  }, [showCreateLeadModal, createLeadInlineContact.domain_id]);

  const onCreateLeadSearchNameChange = (value: string) => {
    setCreateLeadSearchName(value);
    if (createLeadContactId != null || createLeadCustomerId != null) return;
    if (createLeadSearchTimeoutRef.current) clearTimeout(createLeadSearchTimeoutRef.current);
    const q = value.trim();
    if (q.length < 2) {
      setCreateLeadContactResults([]);
      setCreateLeadCustomerResults([]);
      return;
    }
    createLeadSearchTimeoutRef.current = setTimeout(() => {
      Promise.all([
        marketingAPI.searchContacts(q, 15),
        marketingAPI.searchCustomers(q, 15),
      ]).then(([contacts, customers]) => {
        setCreateLeadContactResults(contacts || []);
        setCreateLeadCustomerResults(customers || []);
      }).catch(() => {
        setCreateLeadContactResults([]);
        setCreateLeadCustomerResults([]);
      });
    }, 300);
  };

  const hasCreateLeadSearchResults = createLeadContactResults.length > 0 || createLeadCustomerResults.length > 0;
  const showCreateLeadSearchDropdown = (createLeadContactId == null && createLeadCustomerId == null) && createLeadSearchName.trim().length >= 2 && (hasCreateLeadSearchResults || canCreateContact);

  const handleCreateLeadSubmit = async () => {
    let contactId: number | undefined = createLeadContactId;
    let customerId: number | undefined = createLeadCustomerId;
    // Single source of truth: contact must be created in the same domain/region as the lead.
    const selectedLeadDomainId = createLeadForm.domain_id ?? createLeadInlineContact.domain_id;
    const selectedLeadRegionId = createLeadForm.region_id ?? createLeadInlineContact.region_id;
    let domainId = selectedLeadDomainId;
    let regionId = selectedLeadRegionId;

    if (!contactId && !customerId) {
      const typedName = createLeadSearchName.trim();
      const typedParts = typedName.split(/\s+/).filter(Boolean);
      const fallbackFirst = typedParts[0] || '';
      const fallbackLast = typedParts.slice(1).join(' ') || '';
      const resolvedFirstName = createLeadInlineContact.first_name?.trim() || fallbackFirst;
      const resolvedLastName = createLeadInlineContact.last_name?.trim() || fallbackLast;
      const resolvedPhone = serializePhoneWithCountryCode(createLeadInlineContact.contact_phone_code, createLeadInlineContact.contact_phone)?.trim() || '';
      const resolvedEmail = createLeadInlineContact.contact_email?.trim() || '';
      const hasInline = domainId && Boolean(resolvedFirstName || resolvedLastName || resolvedEmail || resolvedPhone);
      if (hasInline && canCreateContact) {
        setCreateLeadSubmitting(true);
        try {
          let resolvedOrganizationId = createLeadInlineContact.organization_id ?? undefined;
          const typedOrgName = createLeadInlineOrgQuery.trim();
          if (!resolvedOrganizationId && typedOrgName.length >= 2) {
            const orgSearch = await marketingAPI.getOrganizations({ page: 1, page_size: 25, search: typedOrgName, is_active: true });
            const exact = (orgSearch.items || []).find((o) => o.name.trim().toLowerCase() === typedOrgName.toLowerCase());
            if (exact) {
              resolvedOrganizationId = exact.id;
            } else if (canCreateOrg) {
              const createdOrg = await marketingAPI.createOrganization({ name: typedOrgName, is_active: true });
              resolvedOrganizationId = createdOrg.id;
            } else {
              showToast('Organization not found. Please select an existing organization.', 'error');
              setCreateLeadSubmitting(false);
              return;
            }
          }
          const selectedPlant = createLeadInlineContact.plant_id ? createLeadInlinePlants.find(p => p.id === createLeadInlineContact.plant_id) : undefined;
          const contactDomainId = selectedPlant?.domain_id ?? domainId!;
          const contactRegionId = selectedPlant?.region_id ?? regionId ?? undefined;
          const contact = await marketingAPI.createContact({
            domain_id: contactDomainId,
            region_id: contactRegionId,
            organization_id: resolvedOrganizationId,
            plant_id: createLeadInlineContact.plant_id ?? undefined,
            title: createLeadInlineContact.title?.trim() || undefined,
            first_name: resolvedFirstName || undefined,
            last_name: resolvedLastName || undefined,
            contact_email: resolvedEmail || undefined,
            contact_phone: resolvedPhone || undefined,
          });
          setCreateLeadInlineContact((prev) => ({ ...prev, organization_id: resolvedOrganizationId }));
          contactId = contact.id;
        } catch (e: any) {
          showToast(e?.message || 'Failed to create contact', 'error');
          setCreateLeadSubmitting(false);
          return;
        }
      } else {
        showToast('Search and select a contact or company, or fill the new contact form below', 'error');
        return;
      }
    } else if (contactId || customerId) {
      domainId = createLeadForm.domain_id ?? domainId;
      regionId = createLeadForm.region_id ?? regionId;
    }

    if (!domainId) {
      showToast('Domain is required', 'error');
      setCreateLeadSubmitting(false);
      return;
    }

    setCreateLeadSubmitting(true);
    try {
      await marketingAPI.createLead({
        contact_id: contactId,
        customer_id: customerId,
        domain_id: domainId,
        region_id: regionId,
        status_id: createLeadForm.status_id ?? createLeadModalStatusId ?? firstActiveStatusId,
        lead_type_id: createLeadForm.lead_type_id,
        series_code: (typeof window !== 'undefined' && (window.localStorage.getItem(DEFAULT_LEAD_SERIES_STORAGE_KEY) || '').trim()) || undefined,
        notes: createLeadForm.notes?.trim() || undefined,
      });
      showToast('Lead created', 'success');
      closeCreateLeadModal();
      await loadLeads();
    } catch (e: any) {
      showToast(e?.message || 'Failed to create lead', 'error');
    } finally {
      setCreateLeadSubmitting(false);
    }
  };

  const openLeadTypeModal = () => {
    setEditingLeadType(null);
    setLeadTypeForm({ code: '', label: '', display_order: leadTypes.length, is_active: true });
    setShowLeadTypeModal(true);
  };
  const openEditLeadType = (t: LeadTypeOption) => {
    setEditingLeadType(t);
    setLeadTypeForm({ code: t.code, label: t.label, display_order: t.display_order, is_active: t.is_active });
    setShowLeadTypeModal(true);
  };
  const saveLeadType = async () => {
    if (!leadTypeForm.label.trim()) {
      showToast('Label is required', 'error');
      return;
    }
    const code = (leadTypeForm.code || leadTypeForm.label).trim().toLowerCase().replace(/\s+/g, '_');
    if (!code) {
      showToast('Code is required', 'error');
      return;
    }
    setSavingLeadType(true);
    try {
      if (editingLeadType) {
        await marketingAPI.updateLeadType(editingLeadType.id, {
          code,
          label: leadTypeForm.label.trim(),
          display_order: leadTypeForm.display_order,
          is_active: leadTypeForm.is_active,
        });
        showToast('Lead type updated', 'success');
      } else {
        await marketingAPI.createLeadType({
          code,
          label: leadTypeForm.label.trim(),
          display_order: leadTypeForm.display_order,
          is_active: leadTypeForm.is_active,
        });
        showToast('Lead type created', 'success');
      }
      const list = await marketingAPI.getLeadTypes();
      setLeadTypes(list);
      setEditingLeadType(null);
      setLeadTypeForm({ code: '', label: '', display_order: list.length, is_active: true });
    } catch (e: any) {
      showToast(e.message || 'Failed to save lead type', 'error');
    } finally {
      setSavingLeadType(false);
    }
  };
  const confirmDeleteLeadType = async () => {
    if (deleteLeadTypeId == null) return;
    try {
      await marketingAPI.deleteLeadType(deleteLeadTypeId);
      showToast('Lead type deleted', 'success');
      const list = await marketingAPI.getLeadTypes();
      setLeadTypes(list);
      setDeleteLeadTypeId(null);
    } catch (e: any) {
      showToast(e.message || 'Failed to delete lead type', 'error');
    }
  };

  const actions = canCreate ? (
    <Button
      size="sm"
      onClick={() => navigate('/leads/new')}
      leftIcon={<UserPlus size={14} strokeWidth={3} />}
    >
      New Lead
    </Button>
  ) : null;

  if (!canView) {
    return (
      <PageLayout title="Leads" description="Manage marketing leads" breadcrumbs={breadcrumbs}>
        <div className="text-center py-12">
          <p className="text-slate-600">You do not have permission to view leads. Required: marketing.view_lead</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Leads Management"
      description="Track and manage marketing leads"
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      {/* Command Bar */}
      <div className="rounded-2xl px-5 h-14 mb-4 flex items-center gap-0">
        {/* Search */}
        <SearchInput
          placeholder="Search name, company, phone, QTN no., assignee…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          containerClassName="max-w-md shadow-none"
        />

        <div className="w-[1px] h-5 bg-slate-200 mx-4 flex-shrink-0" />

        {/* Filter toggles */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setIncludeWonLost((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-all border ${
              includeWonLost
                ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm'
                : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Trophy size={12} />
            Won &amp; Lost
          </button>

          {((isScopeLoading && reportScope?.can_select_employee) || (reportScope && reportScope.employees.length > 0)) && (
            <>
              <button
                type="button"
                onClick={() => setCreatedByMeOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-all border ${
                  createdByMeOnly
                    ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm'
                    : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <User size={12} />
                Only mine
              </button>

              <button
                type="button"
                onClick={() => setHideEmptyColumns((v) => !v)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-all border ${
                  hideEmptyColumns
                    ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm'
                    : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <LayoutGrid size={12} />
                Hide Empty
              </button>
            </>
          )}

          {/* Employee avatars */}
          {((isScopeLoading && reportScope?.can_select_employee) || (reportScope && reportScope.employees.length > 0)) && (
            <div ref={employeeFilterRef} className="flex items-center gap-1.5 ml-1">
              {isScopeLoading ? (
                <div className="flex items-center gap-1 animate-pulse">
                  <div className="w-7 h-7 rounded-full bg-slate-200" />
                  <div className="w-7 h-7 rounded-full bg-slate-200 -ml-1.5" />
                </div>
              ) : (
                <>
                  <div className="flex items-center -space-x-1.5">
                    {selectedAssignedToIds.slice(0, 4).map((eid) => {
                      const emp = reportScope.employees.find((e) => e.id === eid);
                      return (
                        <Tooltip key={eid} content={emp?.name ?? `Employee ${eid}`}>
                          <div className="w-7 h-7 rounded-full border-2 border-white bg-blue-50 text-blue-700 flex items-center justify-center text-[9px] font-bold shadow-sm ring-1 ring-slate-100">
                            {emp ? getInitials(emp.name) : '?'}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  <Tooltip content="Filter by employee">
                    <button
                      type="button"
                      onClick={() => setShowEmployeeFilterPopover((v) => !v)}
                      className="w-7 h-7 rounded-full border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center transition-all bg-white hover:shadow-sm"
                    >
                      <Plus size={12} strokeWidth={2.5} />
                    </button>
                  </Tooltip>
                </>
              )}
            </div>
          )}
        </div>

        {/* Date range (admin only) */}
        {isHeadOrAdmin && (
          <>
            <div className="w-[1px] h-5 bg-slate-200 mx-4 flex-shrink-0" />
            <div className="flex items-center gap-2 flex-shrink-0">
              {isScopeLoading ? (
                <div className="w-[140px] h-8 bg-slate-100 rounded-lg animate-pulse" />
              ) : (
                <Select
                  value={dateRangePreset}
                  onChange={(v) => handlePresetChange(String(v || 'all'))}
                  options={[
                    { value: 'all', label: 'All Time' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'this_quarter', label: 'This Quarter' },
                    { value: 'custom', label: 'Custom Range...' },
                  ]}
                  placeholder="Select range"
                  containerClassName="w-[140px]"
                  triggerClassName="h-8 text-[11px]"
                  searchable={false}
                  clearable={false}
                />
              )}
              {(appliedDateFrom || appliedDateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setDateFromInput('');
                    setDateToInput('');
                    setAppliedDateFrom('');
                    setAppliedDateTo('');
                    setDateRangePreset('all');
                  }}
                  className="text-[10px] font-bold uppercase tracking-wider text-rose-500 hover:text-rose-700 underline px-1"
                >
                  Clear
                </button>
              )}
            </div>
          </>
        )}

        {/* Manage dropdown + spacer */}
        {(canEdit || canCreate) && (
          <>
            <div className="flex-1" />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-slate-500 border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:text-slate-700 transition-all"
                >
                  <Settings2 size={13} />
                  Manage
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} className="w-52 p-1.5">
                <button
                  type="button"
                  onClick={openStatusModal}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition-colors text-left"
                >
                  <LayoutGrid size={14} className="text-slate-400" />
                  Lead Statuses
                </button>
                <button
                  type="button"
                  onClick={openLeadTypeModal}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition-colors text-left"
                >
                  <Filter size={14} className="text-slate-400" />
                  Lead Types
                </button>
                {canManageNumberSeries && (
                  <button
                    type="button"
                    onClick={() => {
                      setDefaultLeadSeriesCode((typeof window !== 'undefined' && (window.localStorage.getItem(DEFAULT_LEAD_SERIES_STORAGE_KEY) || '').trim()) || '');
                      setShowNumberSeriesModal(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition-colors text-left"
                  >
                    <Hash size={14} className="text-slate-400" />
                    Number Series
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>

      {/* Custom date range row (appears below command bar when "Custom Range" is selected) */}
      {isHeadOrAdmin && dateRangePreset === 'custom' && (
        <div className="flex items-center gap-2 mb-4 animate-in fade-in slide-in-from-left-2 duration-200">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 ml-1">Custom range:</span>
          <DatePicker
            value={dateFromInput}
            onChange={(v) => setDateFromInput(v || '')}
            className="w-[160px]"
            placeholder="From"
            inputSize="sm"
          />
          <span className="text-slate-300 text-[10px] font-bold">to</span>
          <DatePicker
            value={dateToInput}
            onChange={(v) => setDateToInput(v || '')}
            className="w-[160px]"
            placeholder="To"
            inputSize="sm"
          />
          <Button
            size="sm"
            variant="primary"
            className="h-9 px-4 text-xs font-semibold"
            onClick={() => {
              setAppliedDateFrom(dateFromInput);
              setAppliedDateTo(dateToInput);
            }}
          >
            Apply
          </Button>
        </div>
      )}

      {/* Filter Popover - employee multi-select */}
      <FilterPopover
        isOpen={showEmployeeFilterPopover}
        onClose={() => setShowEmployeeFilterPopover(false)}
        triggerRef={employeeFilterRef}
        onClear={() => setSelectedAssignedToIds([])}
      >
        <div className="p-2 min-w-[200px] max-h-[280px] overflow-y-auto">
          <p className="text-xs font-medium text-slate-600 mb-2">Filter by assigned employee</p>
          {reportScope && reportScope.employees.map((emp) => {
            const checked = selectedAssignedToIds.includes(emp.id);
            return (
              <label key={emp.id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    setSelectedAssignedToIds((prev) =>
                      prev.includes(emp.id) ? prev.filter((id) => id !== emp.id) : [...prev, emp.id]
                    );
                  }}
                  className="rounded border-slate-300 text-blue-600"
                />
                <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-medium shrink-0">
                  {getInitials(emp.name)}
                </span>
                <span className="text-sm text-slate-800 truncate">{emp.name}</span>
              </label>
            );
          })}
        </div>
      </FilterPopover>


      <div className="mt-4">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-sm text-slate-600">Loading leads...</p>
          </div>
        ) : (
          <>
            {leadStatuses.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-slate-600 text-sm">Loading statuses...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-24">
                {debouncedSearchTerm.trim() ? (
                  <>
                    <p className="text-slate-400 text-sm font-medium">
                      No results for <span className="font-semibold text-slate-600">&ldquo;{debouncedSearchTerm.trim()}&rdquo;</span>
                    </p>
                    {!includeWonLost && (
                      <p className="mt-2 text-xs text-slate-500">
                        Won and Lost leads are hidden.{' '}
                        <button type="button" onClick={() => setIncludeWonLost(true)} className="font-semibold text-blue-600 hover:underline">
                          Search them too
                        </button>
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="mt-3 text-xs text-blue-600 hover:underline"
                    >
                      Clear search
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-slate-900 font-semibold text-sm uppercase tracking-widest">
                      No leads found
                    </p>
                    {canCreate && (
                      <Button
                        className="mt-4"
                        onClick={() => openCreateLeadModal()}
                        leftIcon={<UserPlus size={14} />}
                      >
                        Create Your First Lead
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-row gap-4 overflow-x-auto pb-4 min-h-[calc(100vh-220px)] -mx-[var(--ui-padding)] px-4" onDragOver={handleBoardDragOver}>
                {statusGroupsForBoard.map(({ groupId, groupLabel, statuses }) => {
                  const visibleStatuses = hideEmptyColumns
                    ? statuses.filter((s) => (leadsByStatus[s.code]?.length ?? 0) > 0)
                    : statuses;
                  if (hideEmptyColumns && visibleStatuses.length === 0) return null;
                  const group = groupId !== 'none' ? leadStatusGroups.find((g) => g.id === groupId) : null;
                  const groupBgStyle = group?.hex_color && /^#[0-9A-Fa-f]{6}$/.test(group.hex_color) ? { backgroundColor: `${group.hex_color}18` } : undefined;
                  const collapseKey = groupId === 'none' ? 'nogroup' : `group-${groupId}`;
                  const isCollapsed = collapsedGroups.has(collapseKey);
                  const groupLeadCount = visibleStatuses.reduce((sum, s) => sum + (leadsByStatus[s.code]?.length ?? 0), 0);
                  return (
                    <div
                      key={groupId === 'none' ? 'nogroup' : groupId}
                      className={`flex flex-shrink-0 flex-col rounded-xl border border-slate-200 overflow-hidden transition-all ${isCollapsed ? 'w-14' : 'p-4'}`}
                      style={groupBgStyle ?? { backgroundColor: 'rgba(241,245,249,0.5)' }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapsed(collapseKey)}
                        className={`flex items-center gap-2 w-full text-left ${isCollapsed ? 'flex-col justify-center py-4 px-1 min-h-[120px] border-b-0' : 'mb-3 border-b border-slate-200 pb-2'}`}
                      >
                        {isCollapsed ? (
                          <>
                            <ChevronLeft size={18} className="text-slate-500 rotate-90" />
                            <span className="text-xs font-semibold text-slate-600 leading-none" style={{ writingMode: 'vertical-rl' }}>{groupLabel}</span>
                            <span className="text-[11px] font-bold text-slate-500 leading-none">{groupLeadCount}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-semibold text-slate-700">{groupLabel}</span>
                            <span className="text-xs text-slate-400">({visibleStatuses.length} status{visibleStatuses.length !== 1 ? 'es' : ''} • {groupLeadCount} leads)</span>
                            <ChevronRight size={16} className="text-slate-400 ml-auto" />
                          </>
                        )}
                      </button>
                      {!isCollapsed && (
                        <div className="flex gap-4 overflow-x-auto pb-2" onDragOver={handleBoardDragOver}>
                          {visibleStatuses.map((status) => {
                            const columnLeads = leadsByStatus[status.code] || [];
                            const useStatusHex = status.hex_color && /^#[0-9A-Fa-f]{6}$/.test(status.hex_color);
                            const statusColor = !useStatusHex ? (STATUS_COLORS[status.code] || DEFAULT_STATUS_COLOR) : null;
                            return (
                              <div
                                key={status.id}
                                className={`flex-shrink-0 w-80 h-[calc(100vh-260px)] rounded-xl border-2 overflow-hidden flex flex-col transition-colors ${dragOverStatusId === status.id ? 'border-blue-500 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'
                                  }`}
                                onDragOver={(e) => handleColumnDragOver(e, status.id)}
                                onDragLeave={handleColumnDragLeave}
                                onDrop={(e) => handleColumnDrop(e, status.id)}
                              >
                                <div
                                  className={`flex-shrink-0 px-3 py-2.5 border-b border-slate-200 flex items-center justify-between ${statusColor ? `${statusColor.bg} ${statusColor.text}` : ''}`}
                                  style={useStatusHex ? { backgroundColor: status.hex_color!, color: getContrastColor(status.hex_color!) } : undefined}
                                >
                                  <span className="font-semibold text-sm uppercase tracking-wide">{status.label}</span>
                                  <span className="flex items-center gap-1">
                                    <span className="text-xs opacity-80">({columnLeads.length})</span>
                                    {canCreate && (
                                      <Tooltip content={`Add lead to ${status.label}`}>
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); openCreateLeadModal(status.id); }}
                                          className="p-1 rounded hover:bg-black/10 transition-colors"
                                        >
                                          <Plus size={16} strokeWidth={2.5} />
                                        </button>
                                      </Tooltip>
                                    )}
                                  </span>
                                </div>
                                <div
                                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 space-y-2 scrollbar-hide"
                                  onDragOver={(e) => handleColumnDragOver(e, status.id)}
                                  onDrop={(e) => handleColumnDrop(e, status.id)}
                                >
                                  {columnLeads.map((lead) => {
                                    const leadDraggable = canEdit && !isWonOrLostLead(lead);
                                    const followUpUrgency = getFollowUpUrgency(lead);
                                    const effectiveFollowUp = getEffectiveFollowUpDate(lead);
                                    const followUpCardClass = ({
                                      overdue: 'bg-red-50 border-red-300 hover:bg-red-100/80',
                                      today: 'bg-orange-50 border-orange-300 hover:bg-orange-100/80',
                                      tomorrow: 'bg-amber-50 border-amber-300 hover:bg-amber-100/80',
                                      week: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100/70',
                                      later: 'bg-blue-50 border-blue-200 hover:bg-blue-100/70',
                                    } as Record<string, string>)[followUpUrgency ?? ''] ?? 'bg-white border-slate-200 hover:shadow-md';
                                    const followUpGlow = ({
                                      overdue: { '--glow-color': 'rgba(239, 68, 68, 0.6)', '--glow-blur': '16px', '--glow-spread': '4px' },
                                      today: { '--glow-color': 'rgba(249, 115, 22, 0.55)', '--glow-blur': '14px', '--glow-spread': '4px' },
                                      tomorrow: { '--glow-color': 'rgba(245, 158, 11, 0.45)', '--glow-blur': '12px', '--glow-spread': '3px' },
                                      week: { '--glow-color': 'rgba(234, 179, 8, 0.35)', '--glow-blur': '10px', '--glow-spread': '3px' },
                                      later: { '--glow-color': 'rgba(59, 130, 246, 0.3)', '--glow-blur': '8px', '--glow-spread': '2px' },
                                    } as Record<string, React.CSSProperties>)[followUpUrgency ?? ''];
                                    return (
                                    <div
                                        key={lead.id}
                                        draggable={leadDraggable}
                                        onDragStart={(e) => handleLeadDragStart(e, lead)}
                                        onDragEnd={handleLeadDragEnd}
                                        onClick={() => canEdit && !didDragRef.current && navigate(`/leads/${lead.id}/edit`)}
                                        style={followUpGlow}
                                        className={`rounded-lg border p-3 shadow-sm transition-all hover:shadow-md ${followUpCardClass} ${followUpGlow ? 'static-followup-glow' : ''} ${leadDraggable ? 'cursor-grab active:cursor-grabbing' : ''} ${draggedLeadId === lead.id ? 'opacity-50' : ''} ${updatingLeadId === lead.id ? 'animate-pulse' : ''}`}
                                      >
                                        {lead.series && (
                                          <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-0.5">{lead.series}</div>
                                        )}
                                        <div className="font-medium text-slate-900 text-sm truncate">
                                          {leadDisplayName(lead)}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate mt-0.5">
                                          {leadDisplayCompany(lead) || <span className="italic text-slate-400">No company</span>}
                                        </div>
                                        {(() => {
                                          const designation = (lead.contact as any)?.designation || (lead.contact as any)?.department;
                                          const location = [(lead.contact as any)?.city, (lead.contact as any)?.state].filter(Boolean).join(', ');
                                          return (designation || location) ? (
                                            <div className="text-[11px] text-slate-500 truncate mt-0.5">
                                              {designation}{designation && location && ' · '}{location}
                                            </div>
                                          ) : null;
                                        })()}
                                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                                          <Building2 size={11} className="text-slate-400 shrink-0" />
                                          <span className="truncate">
                                            {lead.plant?.plant_name || lead.plant?.plant_code || <span className="italic text-slate-400">No plant</span>}
                                          </span>
                                        </div>
                                        {canViewOwner && (
                                          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                                            <User size={11} className="text-slate-400 shrink-0" />
                                            <span className="truncate">
                                              Owner: {(lead as any).assigned_to_name || (lead as any).assigned_to_user?.first_name || (lead as any).assigned_to?.name || lead.assigned_to_username || <span className="italic text-slate-400">Unassigned</span>}
                                            </span>
                                          </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-x-2 mt-1">
                                          <div className="flex items-center gap-1.5 text-xs text-slate-700 font-medium min-w-0">
                                            <Phone size={11} className="text-slate-400 shrink-0" />
                                            <span className="truncate">
                                              {leadDisplayPhone(lead) || <span className="italic text-slate-400 font-normal">Not entered</span>}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1.5 text-xs text-slate-700 font-medium min-w-0">
                                            <Mail size={11} className="text-slate-400 shrink-0" />
                                            <span className="truncate">
                                              {leadDisplayEmail(lead) || <span className="italic text-slate-400 font-normal">Not entered</span>}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-wrap mt-1.5">
                                          {lead.domain?.name && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-100">
                                              {lead.domain.name}
                                            </span>
                                          )}
                                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${lead.region?.name ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-slate-50 text-slate-400 border-slate-100 italic font-normal'}`}>
                                            {lead.region?.name || 'No region'}
                                          </span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 min-w-0 mt-1.5">
                                          <span className="text-slate-400">Next follow-up</span><br />
                                          {effectiveFollowUp
                                            ? (
                                              <span className={({
                                                overdue: 'text-red-600 font-semibold',
                                                today: 'text-orange-600 font-semibold',
                                                tomorrow: 'text-amber-600 font-medium',
                                                week: 'text-yellow-700 font-medium',
                                                later: 'text-blue-600 font-normal',
                                              } as Record<string, string>)[followUpUrgency ?? ''] ?? 'text-slate-500'}>
                                                {new Date(effectiveFollowUp).toLocaleDateString(undefined, { dateStyle: 'short' })} {new Date(effectiveFollowUp).toLocaleTimeString(undefined, { timeStyle: 'short' })}
                                                {followUpUrgency === 'overdue' && ' · Overdue'}
                                                {followUpUrgency === 'today' && ' · Today'}
                                                {followUpUrgency === 'tomorrow' && ' · Tomorrow'}
                                                {followUpUrgency === 'week' && ' · This week'}
                                                {followUpUrgency === 'later' && ' · Upcoming'}
                                              </span>
                                            )
                                            : <span className="italic text-slate-400">Not scheduled</span>}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                                          {lead.quote_number ? (
                                            <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1 py-0.5 truncate max-w-full">
                                              {lead.quote_number}
                                            </span>
                                          ) : lead.quotation_count != null && lead.quotation_count > 0 ? (
                                            <span className="text-[11px] font-medium text-slate-600">
                                              {lead.quotation_count} quotation{lead.quotation_count > 1 ? 's' : ''} uploaded
                                            </span>
                                          ) : (
                                            <span className="text-[11px] italic text-slate-400">No quotation yet</span>
                                          )}
                                          {((lead.quotation_count != null && lead.quotation_count > 0) || lead.quote_number) && (
                                            <Tooltip content="View latest attachment">
                                              <button
                                                type="button"
                                                disabled={viewingAttachmentLeadId === lead.id}
                                                onClick={() => handleViewLatestAttachment(lead)}
                                                className={`shrink-0 text-blue-600 hover:text-blue-800 ${viewingAttachmentLeadId === lead.id ? 'animate-pulse' : ''}`}
                                              >
                                                <Eye size={14} />
                                              </button>
                                            </Tooltip>
                                          )}
                                        </div>
                                        {lead.quote_value != null && lead.quote_value > 0 ? (
                                          <div className="text-xs font-semibold text-blue-700 mt-1.5 inline-flex items-center gap-1 flex-wrap">
                                            <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 px-1 py-0.5 rounded">Quote</span>
                                            {lead.quotation_count != null && lead.quotation_count > 1
                                              ? `${lead.quotation_count} quotes · ₹${Number(lead.quote_value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                              : `1 quote · ₹${Number(lead.quote_value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                          </div>
                                        ) : lead.potential_value != null ? (
                                          <div className="text-xs font-medium text-slate-600 mt-1.5 inline-flex items-center">
                                            <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-1 py-0.5 rounded mr-1">Est</span>
                                            ₹{Number(lead.potential_value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </div>
                                        ) : (
                                          <div className="text-[11px] italic text-slate-400 mt-1.5">No value estimated</div>
                                        )}
                                        {(lead.status_option?.is_final || lead.status_option?.is_lost) && (
                                          <div className={`text-[11px] font-semibold mt-1.5 ${lead.status_option?.is_lost ? 'text-red-600' : 'text-emerald-700'}`}>
                                            {lead.status_option?.is_lost ? 'Lost on ' : 'Won on '}
                                            {/* Lost leads have no closed_at (only Won sets it), so use the last update as the lost date */}
                                            {(() => { const d = lead.status_option?.is_lost ? (lead.closed_at || lead.updated_at) : lead.closed_at; return d ? format(new Date(d), 'dd MMM yyyy') : <span className="italic font-normal text-slate-400">no date</span>; })()}
                                            {!lead.status_option?.is_lost && lead.closed_value != null && lead.closed_value > 0 && ` · ₹${Number(lead.closed_value).toLocaleString('en-IN')}`}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1 mt-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                          {canEdit && wonStatusId && lead.status_id !== wonStatusId && !lead.status_option?.is_lost && (
                                            <Tooltip content="Mark as Won">
                                              <Button
                                                variant="ghost"
                                                size="xs"
                                                onClick={() => openMarkAsWonModal(lead.id)}
                                                className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700"
                                              >
                                                <Trophy size={12} className="mr-0.5" /> Won
                                              </Button>
                                            </Tooltip>
                                          )}
                                          {canEdit && lostStatusId && lead.status_id !== lostStatusId && !lead.status_option?.is_final && (
                                            <Tooltip content="Mark as Lost">
                                              <Button
                                                variant="ghost"
                                                size="xs"
                                                onClick={() => setLeadToMarkLost(lead.id)}
                                                className="h-7 px-2 text-xs text-rose-600 hover:text-rose-700"
                                              >
                                                <XCircle size={12} className="mr-0.5" /> Lost
                                              </Button>
                                            </Tooltip>
                                          )}
                                          {canDelete && (
                                            <Button
                                              variant="ghost"
                                              size="xs"
                                              onClick={() => openDeleteConfirm(lead.id)}
                                              className="h-7 px-2 text-xs text-slate-500 hover:text-rose-700"
                                            >
                                              Delete
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                  ); })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteLeadId != null}
        onClose={() => setDeleteLeadId(null)}
        onConfirm={handleConfirmDeleteLead}
        title="Delete lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />

      <Modal
        isOpen={leadToMarkLost != null}
        onClose={() => { setLeadToMarkLost(null); setMarkLostReason(''); setMarkLostCompetitor(''); setMarkLostPrice(''); }}
        title="Mark lead as Lost"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" size="sm" onClick={() => { setLeadToMarkLost(null); setMarkLostReason(''); setMarkLostCompetitor(''); setMarkLostPrice(''); }} disabled={markLostSubmitting}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={handleMarkAsLostConfirm} disabled={markLostReason.trim().length < 100 || markLostSubmitting}>
              {markLostSubmitting ? 'Saving...' : 'Yes, mark as Lost'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 mb-4">Please provide a detailed reason and the two options below. If you don't know competitor or price, use "Not sure".</p>

        <label className="block text-sm font-medium text-slate-700 mb-1">Competitor name (lost to whom)*</label>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g. ABC Corp or click Not sure"
            value={markLostCompetitor}
            onChange={(e) => setMarkLostCompetitor(e.target.value)}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setMarkLostCompetitor('Not sure')} className="shrink-0">Not sure</Button>
        </div>

        <label className="block text-sm font-medium text-slate-700 mb-1">Lost at (at which price)*</label>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g. ₹50,000 or click Not sure"
            value={markLostPrice}
            onChange={(e) => setMarkLostPrice(e.target.value)}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setMarkLostPrice('Not sure')} className="shrink-0">Not sure</Button>
        </div>

        <label className="block text-sm font-medium text-slate-700 mb-1">Reason why lost (required, min 100 characters)</label>
        <textarea
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[130px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g. Customer chose competitor due to pricing. Budget was cut this quarter. No response after 3 follow-ups."
          value={markLostReason}
          onChange={(e) => setMarkLostReason(e.target.value)}
        />
        <p className={`text-xs mt-1 ${markLostReason.trim().length >= 100 ? 'text-emerald-600' : 'text-slate-500'}`}>
          {markLostReason.trim().length}/100 minimum characters
        </p>
      </Modal>

      <Modal
        isOpen={statusChangePending != null}
        onClose={() => {
          setStatusChangePending(null);
          setStatusChangeForm({ title: '', description: '' });
          setStatusChangeAttachments([{ id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }]);
          setStatusChangeSeriesCode('');
          setStatusChangeBaseQuotations([]);
        }}
        title="Change lead status"
        footer={
          <div className="flex flex-col gap-2 w-full">
            {statusChangeSubmitting && statusChangeUploadProgress !== null && (
              <div className="w-full bg-slate-100 rounded-full h-1 overflow-hidden">
                <div className="bg-blue-600 h-1 rounded-full transition-all duration-300 ease-out" style={{ width: `${statusChangeUploadProgress}%` }}></div>
              </div>
            )}
            <div className="flex justify-end gap-2 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setStatusChangePending(null); setStatusChangeForm({ title: '', description: '' }); setStatusChangeAttachments([{ id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }]); setStatusChangeSeriesCode(''); setStatusChangeBaseQuotations([]); }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleStatusChangeModalSubmit}
                disabled={
                  statusChangeSubmitting ||
                  !statusChangeForm.title.trim() ||
                  !statusChangeForm.description.trim() ||
                  (pendingStatusRequiresAttachment && !statusChangeAttachments.some((a) => a.file))
                }
              >
                {statusChangeSubmitting ? (statusChangeUploadProgress !== null ? `Uploading (${statusChangeUploadProgress}%)...` : 'Updating…') : 'Confirm & change status'}
              </Button>
            </div>
          </div>
        }
      >
        {statusChangePending && (() => {
          const statusChangeLead = leads.find((l) => l.id === statusChangePending.leadId);
          const requiresQuoteNumber = Boolean(pendingToStatus?.set_when_quote_number_generated);
          const requiresAttachment = pendingStatusRequiresAttachment;
          const leadHasNoQuoteNumber = requiresQuoteNumber && !statusChangeLead?.series?.trim();
          return (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-sm text-slate-600">Add mandatory log details for this status change.</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500 block text-xs font-medium mb-0.5">From status</span>
                  <span className="font-medium text-slate-800">
                    {leadStatuses.find((s) => s.id === statusChangePending.currentStatusId)?.label ?? '—'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block text-xs font-medium mb-0.5">To status</span>
                  <span className="font-medium text-slate-800">
                    {leadStatuses.find((s) => s.id === statusChangePending.newStatusId)?.label ?? '—'}
                  </span>
                </div>
              </div>
              {leadHasNoQuoteNumber && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Lead number not set</label>
                  <p className="text-xs text-slate-600">Select a number series and generate the lead number. Quote numbers (for quotation documents) are separate and use their own series.</p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[180px]">
                      <Select
                        placeholder="Number series"
                        value={statusChangeSeriesCode}
                        onChange={(val) => setStatusChangeSeriesCode(String(val ?? ''))}
                        options={[
                          { value: '', label: '— Select series —' },
                          ...seriesList.map((s) => ({ value: s.code ?? '', label: `${s.name} (${s.code})` })),
                        ]}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!statusChangeSeriesCode.trim() || statusChangeGeneratingQuote}
                      onClick={handleStatusChangeGenerateQuote}
                    >
                      {statusChangeGeneratingQuote ? 'Generating…' : 'Generate lead number'}
                    </Button>
                  </div>
                </div>
              )}
              {statusChangeLead?.series?.trim() && (
                <p className="text-xs text-slate-600">Lead number: <strong>{statusChangeLead.series}</strong></p>
              )}
              <Input
                label="Reason / title *"
                placeholder="e.g. Customer confirmed requirements"
                value={statusChangeForm.title}
                onChange={(e) => setStatusChangeForm((f) => ({ ...f, title: e.target.value }))}
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes *</label>
                <textarea
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional details..."
                  value={statusChangeForm.description}
                  onChange={(e) => setStatusChangeForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">File attachments {requiresAttachment ? '*' : '(optional)'}</label>
                <p className="text-xs text-slate-500 mb-2">
                  {requiresAttachment ? 'At least one attachment is required to change to this status.' : 'You can attach files for this status change.'}
                </p>
                <div className="space-y-2">
                  {statusChangeAttachments.map((row) => (
                    <div key={row.id} className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 hover:bg-slate-50 shrink-0">
                          <Upload size={12} />
                          <span className="truncate max-w-[120px]">{row.file ? row.file.name : 'Choose file'}</span>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              setStatusChangeAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, file: file ?? null } : r)));
                            }}
                          />
                        </label>
                        <select
                          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs w-32"
                          value={row.kind}
                          onChange={(e) => setStatusChangeAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, kind: e.target.value as 'new-quotation' | 'revise-quotation' | 'attachment', reviseTargetQuotation: '' } : r)))}
                        >
                          <option value="new-quotation">New Quotation</option>
                          <option value="revise-quotation">Revise Quotation</option>
                          <option value="attachment">Attachment</option>
                        </select>
                        {row.kind === 'revise-quotation' && statusChangeBaseQuotations.length > 0 && (
                          <div className="w-40 shrink-0 [&_button]:!h-9 [&_button]:!min-h-0 [&_button]:!py-0">
                            <Select
                              options={statusChangeBaseQuotations.map((q) => ({ value: q, label: q }))}
                              value={row.reviseTargetQuotation}
                              onChange={(val) => setStatusChangeAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, reviseTargetQuotation: String(val ?? '') } : r)))}
                              placeholder="Which quotation?"
                              className="w-full"
                              searchable
                            />
                          </div>
                        )}
                        {row.kind === 'attachment' && (
                          <input
                            className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs min-w-[100px] flex-1 max-w-[160px]"
                            placeholder="Title"
                            value={row.title}
                            onChange={(e) => setStatusChangeAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, title: e.target.value } : r)))}
                          />
                        )}
                        {row.kind !== 'attachment' && (
                          <CurrencyInput
                            placeholder={row.kind === 'revise-quotation' ? 'Revised Quote Value (₹)' : 'Quote Value (₹)'}
                            value={row.quoteValue}
                            onChange={(val) => setStatusChangeAttachments((prev) => prev.map((r) => (r.id === row.id ? { ...r, quoteValue: val } : r)))}
                            inputSize="sm"
                            containerClassName="min-w-[110px] max-w-[150px] !space-y-0"
                          />
                        )}
                        <Tooltip content="Remove">
                          <button
                            type="button"
                            onClick={() => setStatusChangeAttachments((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== row.id) : prev))}
                            className="p-1.5 rounded text-slate-400 hover:bg-slate-200 hover:text-rose-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </Tooltip>
                      </div>
                      {row.kind === 'revise-quotation' && (
                        <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                          This value won't be reflected in the kanban quotation bar
                        </p>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    onClick={() => setStatusChangeAttachments((prev) => [...prev, { id: crypto.randomUUID(), kind: 'attachment', file: null, title: '', quoteValue: '', reviseTargetQuotation: '' }])}
                  >
                    <Plus size={12} /> Add file
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        isOpen={showWonClosedValueModal}
        onClose={() => {
          setShowWonClosedValueModal(false);
          setPendingWonLeadId(null);
          setPendingWonStatusId(null);
          setClosedValueInput('');
          setWonPoFile(null);
          setWonDateInput('');
        }}
        title="Closed value (required)"
        footer={
          <div className="flex flex-col gap-2 w-full">
            {updatingLeadId !== null && wonPoUploadProgress !== null && (
              <div className="w-full bg-slate-100 rounded-full h-1 overflow-hidden">
                <div className="bg-blue-600 h-1 rounded-full transition-all duration-300 ease-out" style={{ width: `${wonPoUploadProgress}%` }}></div>
              </div>
            )}
            <div className="flex justify-end gap-2 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowWonClosedValueModal(false);
                  setPendingWonLeadId(null);
                  setPendingWonStatusId(null);
                  setClosedValueInput('');
                  setWonPoFile(null);
                  setWonDateInput('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleWonClosedValueSubmit}
                disabled={updatingLeadId !== null || !closedValueInput.trim() || !wonPoFile || (canCreate && wonDateInput > getTodayDateInputValue())}
              >
                {updatingLeadId !== null ? (wonPoUploadProgress !== null ? `Uploading (${wonPoUploadProgress}%)...` : 'Submitting...') : 'Submit'}
              </Button>
            </div>
          </div>
        }
      >
        <p className="text-sm text-slate-600 mb-3">
          This status is marked as Won. Enter the actual closed deal value (required).
        </p>
        <CurrencyInput
          allowDecimal
          label="Closed value"
          placeholder="e.g. 50,000"
          value={closedValueInput}
          onChange={setClosedValueInput}
          containerClassName="max-w-xs"
        />
        {canCreate && (
          <div className="mt-3 max-w-xs">
            <DatePicker
              label="Won date"
              value={wonDateInput}
              onChange={(v) => setWonDateInput(v || '')}
              maxDate={getTodayDateInputValue()}
              inputSize="sm"
            />
            <p className="mt-1 text-xs text-slate-500">
              Defaults to today. Only change this to backdate an older lead that was actually won earlier, so it counts toward that quarter.
            </p>
          </div>
        )}
        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700 mb-1">PO file (required)</label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
            className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:text-xs"
            onChange={(e) => setWonPoFile(e.target.files?.[0] ?? null)}
          />
          <p className="mt-1 text-xs text-slate-500">{wonPoFile ? `Selected: ${wonPoFile.name}` : 'Upload purchase order file to proceed.'}</p>
        </div>
      </Modal>

      <Modal
        isOpen={showCreateLeadModal}
        onClose={closeCreateLeadModal}
        title="New Lead"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" size="sm" onClick={closeCreateLeadModal}>Cancel</Button>
            <Button size="sm" onClick={handleCreateLeadSubmit} disabled={createLeadSubmitting}>
              {createLeadSubmitting ? 'Creating...' : 'Create Lead'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {createLeadModalStatusId != null && (
            <div className="text-sm text-slate-600">
              Status: <strong>{leadStatuses.find((s) => s.id === createLeadModalStatusId)?.label ?? '—'}</strong>
            </div>
          )}
          <p className="text-xs text-slate-500">Type a contact name or company name to search. Select from the list (contacts and customers in your region), or fill the form below to create a new contact.</p>
          <div className="relative">
            <Input
              label="Search by contact name or company name"
              value={createLeadSearchName}
              onChange={(e) => onCreateLeadSearchNameChange(e.target.value)}
              onBlur={() => setTimeout(() => { setCreateLeadContactResults([]); setCreateLeadCustomerResults([]); }, 200)}
              placeholder="Type to search..."
            />
            {showCreateLeadSearchDropdown && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                {createLeadContactResults.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-slate-500 px-3 py-2 border-b border-slate-100 bg-slate-50">Contacts</p>
                    {createLeadContactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex flex-col gap-0.5"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCreateLeadContactId(c.id);
                          setCreateLeadCustomerId(undefined);
                          setCreateLeadSearchName([c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.contact_person_name || c.organization?.name || '');
                          setCreateLeadContactResults([]);
                          setCreateLeadCustomerResults([]);
                          setCreateLeadForm((f) => ({ ...f, domain_id: c.domain_id ?? f.domain_id, region_id: c.region_id ?? f.region_id }));
                        }}
                      >
                        <span className="font-medium text-slate-800">{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.contact_person_name || c.organization?.name || 'Contact'}</span>
                        <span className="text-slate-500 text-xs">{[c.contact_email, c.contact_phone, c.organization?.name].filter(Boolean).join(' · ')}</span>
                      </button>
                    ))}
                  </>
                )}
                {createLeadCustomerResults.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-slate-500 px-3 py-2 border-b border-slate-100 bg-slate-50">Customers</p>
                    {createLeadCustomerResults.map((cust) => (
                      <button
                        key={cust.id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex flex-col gap-0.5"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCreateLeadCustomerId(cust.id);
                          setCreateLeadContactId(undefined);
                          setCreateLeadSearchName(cust.company_name || '');
                          setCreateLeadContactResults([]);
                          setCreateLeadCustomerResults([]);
                          setCreateLeadForm((f) => ({ ...f, domain_id: cust.domain_id ?? f.domain_id, region_id: cust.region_id ?? f.region_id }));
                        }}
                      >
                        <span className="font-medium text-slate-800">{cust.company_name}</span>
                        {cust.organization?.name && <span className="text-slate-500 text-xs">{cust.organization.name}</span>}
                      </button>
                    ))}
                  </>
                )}
                {!hasCreateLeadSearchResults && createLeadSearchName.trim().length >= 2 && canCreateContact && (
                  <p className="text-xs text-slate-500 px-3 py-2 border-t border-slate-100">No matches. Fill the form below to create a new contact.</p>
                )}
              </div>
            )}
          </div>
          {(createLeadContactId != null || createLeadCustomerId != null) && (
            <div className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded-lg border border-slate-200">
              <span className="text-slate-600">{createLeadContactId != null ? 'Linked to contact' : 'Linked to customer'}</span>
              <Tooltip content="Unlink">
                <button
                  type="button"
                  onClick={() => { setCreateLeadContactId(undefined); setCreateLeadCustomerId(undefined); }}
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-rose-600 p-0.5"
                >
                  <X size={14} /> Unlink
                </button>
              </Tooltip>
            </div>
          )}
          {createLeadContactId == null && createLeadCustomerId == null && (
            <div className="space-y-3 p-3 bg-slate-50/50 rounded-lg border border-slate-200 border-dashed">
              <h4 className="text-sm font-semibold text-slate-700">Or create new contact</h4>
              <p className="text-xs text-slate-500">Fill below to create a contact and link this lead. Domain and at least name + phone or email are required.</p>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Domain <span className="text-red-500">*</span></label>
                  <select
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={createLeadInlineContact.domain_id ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : undefined;
                      setCreateLeadInlineContact((prev) => ({ ...prev, domain_id: val, region_id: undefined }));
                      setCreateLeadForm((prev) => ({ ...prev, domain_id: val, region_id: undefined }));
                    }}
                  >
                    <option value="">Select domain</option>
                    {createLeadDomains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Region</label>
                  <select
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    value={createLeadInlineContact.region_id ?? ''}
                    onChange={(e) => {
                      const val = e.target.value ? Number(e.target.value) : undefined;
                      setCreateLeadInlineContact((prev) => ({ ...prev, region_id: val }));
                      setCreateLeadForm((prev) => ({ ...prev, region_id: val }));
                    }}
                    disabled={!createLeadInlineContact.domain_id}
                  >
                    <option value="">— None —</option>
                    {createLeadRegions.filter((r) => !createLeadInlineContact.domain_id || r.domain_id === createLeadInlineContact.domain_id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Organization (optional)</label>
                  <Input
                    value={createLeadInlineOrgQuery}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCreateLeadInlineOrgQuery(v);
                      setCreateLeadInlineContact((prev) => ({ ...prev, organization_id: undefined, plant_id: undefined }));
                      setCreateLeadInlinePlants([]);
                      if (createLeadOrgTimeoutRef.current) clearTimeout(createLeadOrgTimeoutRef.current);
                      if (v.trim().length < 2) { setCreateLeadInlineOrgSuggestions([]); return; }
                      createLeadOrgTimeoutRef.current = setTimeout(() => {
                        marketingAPI.getOrganizations({ page: 1, page_size: 15, search: v.trim(), is_active: true })
                          .then((res) => setCreateLeadInlineOrgSuggestions(res.items ?? []))
                          .catch(() => setCreateLeadInlineOrgSuggestions([]));
                      }, 300);
                    }}
                    onBlur={() => setTimeout(() => setCreateLeadInlineOrgSuggestions([]), 150)}
                    placeholder="Type to search organization"
                  />
                  {createLeadInlineOrgSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-auto">
                      {createLeadInlineOrgSuggestions.map((org) => (
                        <button
                          key={org.id}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCreateLeadInlineContact((prev) => ({ ...prev, organization_id: org.id, plant_id: undefined }));
                            setCreateLeadInlineOrgQuery(org.name);
                            setCreateLeadInlineOrgSuggestions([]);
                            marketingAPI.getOrganizationPlants(org.id).then((pl) => setCreateLeadInlinePlants(pl)).catch(() => setCreateLeadInlinePlants([]));
                          }}
                        >
                          {org.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <div className="w-20 shrink-0">
                    <Select label="Title" options={NAME_PREFIXES} value={createLeadInlineContact.title} onChange={(v) => setCreateLeadInlineContact((prev) => ({ ...prev, title: (v ?? '') as string }))} placeholder="—" />
                  </div>
                  <div className="flex-1 min-w-0"><Input label="First name" value={createLeadInlineContact.first_name} onChange={(e) => setCreateLeadInlineContact((prev) => ({ ...prev, first_name: e.target.value }))} placeholder="First name" /></div>
                  <div className="flex-1 min-w-0"><Input label="Last name" value={createLeadInlineContact.last_name} onChange={(e) => setCreateLeadInlineContact((prev) => ({ ...prev, last_name: e.target.value }))} placeholder="Last name" /></div>
                </div>
                <Input label="Email" type="email" value={createLeadInlineContact.contact_email} onChange={(e) => setCreateLeadInlineContact((prev) => ({ ...prev, contact_email: e.target.value }))} placeholder="email@example.com" />
                <div className="flex gap-2 items-end">
                  <div className="w-32 shrink-0">
                    <Select label="Phone code" options={COUNTRY_CODES} value={createLeadInlineContact.contact_phone_code} onChange={(v) => setCreateLeadInlineContact((prev) => ({ ...prev, contact_phone_code: (v ?? DEFAULT_COUNTRY_CODE) as string }))} placeholder="Code" searchable getSearchText={getCountryCodeSearchText} getOptionKey={(o) => o.label} />
                  </div>
                  <div className="flex-1 min-w-0"><Input label="Phone" value={createLeadInlineContact.contact_phone} onChange={(e) => setCreateLeadInlineContact((prev) => ({ ...prev, contact_phone: e.target.value }))} placeholder="Number" /></div>
                </div>
                {createLeadInlineContact.organization_id != null && createLeadInlinePlants.length > 0 && (
                  <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Plant / Location</label>
                    <Select
                      options={[{ value: '', label: 'None' }, ...createLeadInlinePlants.map((p) => ({ value: String(p.id), label: p.plant_name || `Plant ${p.id}` }))]}
                      value={createLeadInlineContact.plant_id != null ? String(createLeadInlineContact.plant_id) : ''}
                      onChange={(val) => {
                        const newPlantId = val ? Number(val) : undefined;
                        const selectedPlant = newPlantId ? createLeadInlinePlants.find(p => p.id === newPlantId) : undefined;
                        setCreateLeadInlineContact((prev) => ({
                          ...prev,
                          plant_id: newPlantId,
                          domain_id: selectedPlant?.domain_id ?? prev.domain_id,
                          region_id: selectedPlant?.region_id ?? prev.region_id,
                        }));
                      }}
                      placeholder="Select plant"
                      searchable
                    />
                    {createLeadInlineContact.plant_id && (() => {
                      const sp = createLeadInlinePlants.find(p => p.id === createLeadInlineContact.plant_id);
                      if (!sp) return null;
                      const pd = createLeadDomains.find(d => d.id === sp.domain_id);
                      const pr = createLeadRegions.find(r => r.id === sp.region_id);
                      const addr = [sp.address_line1, sp.address_line2, sp.city, sp.state, sp.country, sp.postal_code].filter(Boolean).join(', ');
                      return (
                        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-sm mt-2 animate-in zoom-in-95 duration-200 shadow-sm">
                          <p className="font-bold text-slate-800">Selected plant</p>
                          <p className="text-slate-600 mt-0.5">{sp.plant_name}</p>
                          {addr && <p className="text-slate-500 text-xs mt-1">{addr}</p>}
                          {(pd || pr) && (
                            <p className="text-slate-500 text-xs mt-0.5">
                              {pd?.name}{pd && pr ? ' · ' : ''}{pr?.name}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <Select
              value={createLeadForm.status_id != null ? String(createLeadForm.status_id) : (createLeadModalStatusId != null ? String(createLeadModalStatusId) : (firstActiveStatusId != null ? String(firstActiveStatusId) : ''))}
              onChange={(val) => setCreateLeadForm((f) => ({ ...f, status_id: val ? Number(val) : undefined }))}
              options={[{ value: '', label: '— Default —' }, ...leadStatuses.filter((s) => s.is_active).map((s) => ({ value: String(s.id), label: s.label }))]}
              placeholder="Status"
            />
          </div>
          <p className="text-xs text-slate-500">Lead number uses the series assigned via the &quot;Number series&quot; button above, or the backend default.</p>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-700 ml-0.5">Notes</label>
            <textarea
              value={createLeadForm.notes}
              onChange={(e) => setCreateLeadForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Notes"
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-600"
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showNumberSeriesModal}
        onClose={() => setShowNumberSeriesModal(false)}
        title="Assign number series for new leads"
        contentClassName="max-w-md"
      >
        <p className="text-sm text-slate-600 mb-3">Choose which number series to use when creating new leads. New leads will get their lead number from this series.</p>
        <div className="space-y-3">
          <Select
            label="Lead number series"
            options={[
              { value: '', label: '— Use backend default (LEAD_SERIES_CODE) —' },
              ...seriesList
                .filter((s) => (s.entity_type ?? '').toLowerCase() === 'lead' || s.code === 'lead_number' || !s.entity_type)
                .map((s) => ({ value: s.code ?? '', label: `${s.name} (${s.code})` })),
            ]}
            value={defaultLeadSeriesCode}
            onChange={(val) => setDefaultLeadSeriesCode((val != null && val !== '') ? String(val) : '')}
            placeholder="Select series"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowNumberSeriesModal(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  if (defaultLeadSeriesCode.trim()) {
                    window.localStorage.setItem(DEFAULT_LEAD_SERIES_STORAGE_KEY, defaultLeadSeriesCode.trim());
                  } else {
                    window.localStorage.removeItem(DEFAULT_LEAD_SERIES_STORAGE_KEY);
                  }
                }
                setShowNumberSeriesModal(false);
                showToast(defaultLeadSeriesCode.trim() ? `New leads will use series: ${defaultLeadSeriesCode.trim()}` : 'New leads will use backend default series.', 'success');
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setEditingStatus(null);
          setEditingGroup(null);
          setAddingGroup(false);
          setStatusForm({ code: '', label: '', display_order: 0, group_id: undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false });
          setGroupForm({ code: '', label: '', expected_duration_days: undefined, follow_up_interval_days: undefined, display_order: 0, is_active: true, hex_color: '' });
        }}
        title="Lead statuses & groups"
        contentClassName="max-w-6xl"
      >
        <div className="max-h-[85vh] overflow-y-auto overflow-x-hidden pr-1">
          {/* Status groups — inline add/edit in table */}
          <h4 className="text-sm font-semibold text-slate-700 mb-1">1. Status groups</h4>
          <p className="text-xs text-slate-500 mb-2">Click &quot;Create group&quot; to add a row; edit or save inline in the table.</p>
          <div className="overflow-x-auto border border-slate-200 rounded-lg mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-2 py-1.5">Code</th>
                  <th className="px-2 py-1.5">Label</th>
                  <th className="px-2 py-1.5" title="Expected duration">Days</th>
                  <th className="px-2 py-1.5" title="Follow-up task every N days">Follow-up</th>
                  <th className="px-2 py-1.5">Order</th>
                  <th className="px-2 py-1.5">Active</th>
                  <th className="px-2 py-1.5">Color</th>
                  <th className="px-2 py-1.5">
                    {canEdit && (
                      <Button variant="ghost" size="xs" className="text-blue-600" onClick={() => { setAddingGroup(true); setEditingGroup(null); setGroupForm({ code: '', label: '', expected_duration_days: undefined, follow_up_interval_days: undefined, display_order: leadStatusGroups.length, is_active: true, hex_color: '' }); }} leftIcon={<Plus size={12} />}>
                        Create group
                      </Button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {canEdit && addingGroup && (
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <td className="px-2 py-1 align-middle">
                      <input className="h-8 w-full max-w-[8rem] rounded border border-slate-200 bg-white px-2 text-sm font-mono focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" placeholder="e.g. follow_up" value={groupForm.code} onChange={(e) => setGroupForm((f) => ({ ...f, code: e.target.value }))} />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <input className="h-8 w-full max-w-[8rem] rounded border border-slate-200 bg-white px-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" placeholder="e.g. Follow-up" value={groupForm.label} onChange={(e) => setGroupForm((f) => ({ ...f, label: e.target.value }))} />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <input type="number" className="h-8 w-14 rounded border border-slate-200 bg-white px-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" placeholder="—" value={groupForm.expected_duration_days ?? ''} onChange={(e) => setGroupForm((f) => ({ ...f, expected_duration_days: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0 }))} />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <input type="number" className="h-8 w-14 rounded border border-slate-200 bg-white px-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" placeholder="—" title="Task every N days" value={groupForm.follow_up_interval_days ?? ''} onChange={(e) => setGroupForm((f) => ({ ...f, follow_up_interval_days: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0 }))} />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <input type="number" className="h-8 w-14 rounded border border-slate-200 bg-white px-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" value={groupForm.display_order} onChange={(e) => setGroupForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <label className="flex h-8 cursor-pointer items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={groupForm.is_active} onChange={(e) => setGroupForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600" />
                        <span>Active</span>
                      </label>
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <div className="flex items-center gap-1.5">
                        <input type="color" className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5" value={groupForm.hex_color && /^#[0-9A-Fa-f]{6}$/.test(groupForm.hex_color) ? groupForm.hex_color : '#3b82f6'} onChange={(e) => setGroupForm((f) => ({ ...f, hex_color: e.target.value }))} title="Color" />
                        <input className="h-8 w-20 rounded border border-slate-200 bg-white px-2 text-sm font-mono focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200" placeholder="#3b82f6" value={groupForm.hex_color} onChange={(e) => setGroupForm((f) => ({ ...f, hex_color: e.target.value }))} title="Hex color" />
                      </div>
                    </td>
                    <td className="px-2 py-1 align-middle">
                      <div className="flex gap-1">
                        <Button size="xs" onClick={saveGroup} disabled={savingGroup || !groupForm.label?.trim()}>{savingGroup ? '...' : 'Save'}</Button>
                        <Button size="xs" variant="outline" onClick={cancelGroupForm}>Cancel</Button>
                      </div>
                    </td>
                  </tr>
                )}
                {leadStatusGroups.map((g) =>
                  canEdit && editingGroup?.id === g.id ? (
                    <tr key={g.id} className="bg-amber-50/50 border-b border-slate-200">
                      <td className="px-2 py-1.5">
                        <input className="w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono" placeholder="Code" value={groupForm.code} onChange={(e) => setGroupForm((f) => ({ ...f, code: e.target.value }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input className="w-full rounded border border-slate-300 px-2 py-1 text-sm" placeholder="Label" value={groupForm.label} onChange={(e) => setGroupForm((f) => ({ ...f, label: e.target.value }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" className="w-14 rounded border border-slate-300 px-2 py-1 text-sm" value={groupForm.expected_duration_days ?? ''} onChange={(e) => setGroupForm((f) => ({ ...f, expected_duration_days: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0 }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" className="w-14 rounded border border-slate-300 px-2 py-1 text-sm" title="Follow-up task every N days" value={groupForm.follow_up_interval_days ?? ''} onChange={(e) => setGroupForm((f) => ({ ...f, follow_up_interval_days: e.target.value === '' ? undefined : parseInt(e.target.value, 10) || 0 }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" className="w-14 rounded border border-slate-300 px-2 py-1 text-sm" value={groupForm.display_order} onChange={(e) => setGroupForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                      </td>
                      <td className="px-2 py-1.5">
                        <label className="flex items-center gap-1">
                          <input type="checkbox" checked={groupForm.is_active} onChange={(e) => setGroupForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600" />
                          <span className="text-xs">Active</span>
                        </label>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <input type="color" className="h-8 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5" value={groupForm.hex_color && /^#[0-9A-Fa-f]{6}$/.test(groupForm.hex_color) ? groupForm.hex_color : '#3b82f6'} onChange={(e) => setGroupForm((f) => ({ ...f, hex_color: e.target.value }))} title="Color" />
                          <input className="w-20 rounded border border-slate-300 px-2 py-1 text-sm font-mono" placeholder="#3b82f6" value={groupForm.hex_color} onChange={(e) => setGroupForm((f) => ({ ...f, hex_color: e.target.value }))} title="Hex color" />
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1">
                          <Button size="xs" onClick={saveGroup} disabled={savingGroup || !groupForm.label?.trim()}>{savingGroup ? '...' : 'Save'}</Button>
                          <Button size="xs" variant="outline" onClick={cancelGroupForm}>Cancel</Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={g.id} className="border-b border-slate-100">
                      <td className="px-2 py-1 font-mono text-slate-700">{g.code}</td>
                      <td className="px-2 py-1 align-middle">{g.label}</td>
                      <td className="px-2 py-1 align-middle">{g.expected_duration_days ?? '—'}</td>
                      <td className="px-2 py-1 align-middle">{g.follow_up_interval_days ?? '—'}</td>
                      <td className="px-2 py-1 align-middle">{g.display_order}</td>
                      <td className="px-2 py-1 align-middle">{g.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 align-middle">
                        {g.hex_color ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-5 w-5 rounded border border-slate-300 shrink-0" style={{ backgroundColor: g.hex_color }} title={g.hex_color} />
                            <span className="text-xs font-mono text-slate-600">{g.hex_color}</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1 align-middle">
                        {canEdit && (
                          <>
                            <Button variant="ghost" size="xs" className="text-blue-600" onClick={() => openAddStatusToGroup(g.id)} leftIcon={<Plus size={12} />}>Add status</Button>
                            <Button variant="ghost" size="xs" onClick={() => { setEditingGroup(g); setAddingGroup(false); setGroupForm({ code: g.code, label: g.label, expected_duration_days: g.expected_duration_days ?? undefined, follow_up_interval_days: g.follow_up_interval_days ?? undefined, display_order: g.display_order, is_active: g.is_active, hex_color: g.hex_color ?? '' }); }}>Edit</Button>
                            <Button variant="ghost" size="xs" className="text-rose-600" onClick={() => setDeleteGroupId(g.id)}>Delete</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Statuses — card rows grouped by status group */}
          <h4 className="text-sm font-semibold text-slate-700 mb-1 mt-4">2. Statuses</h4>
          <p className="text-xs text-slate-500 mb-2">Organize your pipeline stages. Each status can be marked as Won, Lost, or Hot.</p>
          <div className="space-y-3">
            {(() => {
              const byGroup = new Map<number | 'none', LeadStatusOption[]>();
              leadStatuses.forEach((s) => {
                const key = s.group_id ?? 'none';
                if (!byGroup.has(key)) byGroup.set(key, []);
                byGroup.get(key)!.push(s);
              });
              const groupOrder = [...leadStatusGroups.filter((g) => g.is_active).map((g) => g.id), 'none' as const];
              return groupOrder.map((groupId) => {
                const statuses = byGroup.get(groupId) ?? [];
                if (statuses.length === 0 && groupId === 'none') return null;
                const groupObj = groupId === 'none' ? null : leadStatusGroups.find((g) => g.id === groupId);
                const groupLabel = groupId === 'none' ? 'Ungrouped' : groupObj?.label ?? `Group #${groupId}`;
                const groupColor = groupObj?.hex_color || '#64748b';
                return (
                  <div key={groupId === 'none' ? 'nogroup' : groupId} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* Group header */}
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: groupColor }} />
                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{groupLabel}</span>
                        <span className="text-[10px] text-slate-400 font-medium">{statuses.length} status{statuses.length !== 1 ? 'es' : ''}</span>
                      </div>
                      {groupId !== 'none' && canEdit && (
                        <Button variant="ghost" size="xs" className="text-blue-600 h-6" onClick={() => openAddStatusToGroup(groupId)} leftIcon={<Plus size={11} />}>
                          Add
                        </Button>
                      )}
                    </div>
                    {/* Inline add form */}
                    {canEdit && statusForm.group_id === groupId && !editingStatus && (
                      <div className="px-3 py-2.5 bg-blue-50/40 border-b border-blue-100">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <input type="color" className="h-7 w-7 cursor-pointer rounded border border-slate-200 bg-white p-0.5 shrink-0" value={statusForm.hex_color && /^#[0-9A-Fa-f]{6}$/.test(statusForm.hex_color) ? statusForm.hex_color : '#3b82f6'} onChange={(e) => setStatusForm((f) => ({ ...f, hex_color: e.target.value }))} />
                            <input className="h-7 w-full max-w-[7rem] rounded border border-slate-200 bg-white px-2 text-xs font-mono focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="code" value={statusForm.code} onChange={(e) => setStatusForm((f) => ({ ...f, code: e.target.value }))} />
                          </div>
                          <input className="h-7 w-full max-w-[10rem] rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Label name" value={statusForm.label} onChange={(e) => setStatusForm((f) => ({ ...f, label: e.target.value }))} />
                          <input type="number" className="h-7 w-12 rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" title="Order" value={statusForm.display_order} onChange={(e) => setStatusForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                          <div className="flex items-center gap-3 text-xs text-slate-600">
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_active} onChange={(e) => setStatusForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Active</span></label>
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_final} onChange={(e) => setStatusForm((f) => ({ ...f, is_final: e.target.checked }))} className="rounded border-slate-300 text-emerald-600 w-3.5 h-3.5" /><span className="text-emerald-700">Won</span></label>
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_lost} onChange={(e) => setStatusForm((f) => ({ ...f, is_lost: e.target.checked }))} className="rounded border-slate-300 text-red-600 w-3.5 h-3.5" /><span className="text-red-600">Lost</span></label>
                            <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_hot} onChange={(e) => setStatusForm((f) => ({ ...f, is_hot: e.target.checked }))} className="rounded border-slate-300 text-amber-500 w-3.5 h-3.5" /><span className="text-amber-600">Hot</span></label>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <Tooltip content="Auto-set when quotation added"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.set_when_quotation_added} onChange={(e) => setStatusForm((f) => ({ ...f, set_when_quotation_added: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Quote+</span></label></Tooltip>
                            <Tooltip content="Auto-set when lead # generated"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.set_when_quote_number_generated} onChange={(e) => setStatusForm((f) => ({ ...f, set_when_quote_number_generated: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Lead#</span></label></Tooltip>
                            <Tooltip content="Require attachment on Kanban move"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.attachment_required_on_kanban_change} onChange={(e) => setStatusForm((f) => ({ ...f, attachment_required_on_kanban_change: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Attach req.</span></label></Tooltip>
                          </div>
                          <div className="flex gap-1 ml-auto">
                            <Button size="xs" onClick={saveStatus} disabled={savingStatus || !statusForm.label?.trim()}>{savingStatus ? '...' : 'Save'}</Button>
                            <Button size="xs" variant="outline" onClick={() => { setEditingStatus(null); setStatusForm({ code: '', label: '', display_order: 0, group_id: undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false }); }}>Cancel</Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Status rows */}
                    {statuses.length === 0 && !(canEdit && statusForm.group_id === groupId && !editingStatus) ? (
                      <div className="px-3 py-4 text-center text-xs text-slate-400 italic">No statuses yet. Click &quot;Add&quot; above.</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {statuses.map((s) =>
                          canEdit && editingStatus?.id === s.id ? (
                            <div key={s.id} className="px-3 py-2.5 bg-amber-50/50 border-l-2 border-amber-300">
                              <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex items-center gap-1.5">
                                  <input type="color" className="h-7 w-7 cursor-pointer rounded border border-slate-200 bg-white p-0.5 shrink-0" value={statusForm.hex_color && /^#[0-9A-Fa-f]{6}$/.test(statusForm.hex_color) ? statusForm.hex_color : '#3b82f6'} onChange={(e) => setStatusForm((f) => ({ ...f, hex_color: e.target.value }))} />
                                  <input className="h-7 w-full max-w-[7rem] rounded border border-slate-200 bg-white px-2 text-xs font-mono focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="code" value={statusForm.code} onChange={(e) => setStatusForm((f) => ({ ...f, code: e.target.value }))} />
                                </div>
                                <input className="h-7 w-full max-w-[10rem] rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Label" value={statusForm.label} onChange={(e) => setStatusForm((f) => ({ ...f, label: e.target.value }))} />
                                <input type="number" className="h-7 w-12 rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" title="Order" value={statusForm.display_order} onChange={(e) => setStatusForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                                <div className="flex items-center gap-3 text-xs text-slate-600">
                                  <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_active} onChange={(e) => setStatusForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Active</span></label>
                                  <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_final} onChange={(e) => setStatusForm((f) => ({ ...f, is_final: e.target.checked }))} className="rounded border-slate-300 text-emerald-600 w-3.5 h-3.5" /><span className="text-emerald-700">Won</span></label>
                                  <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_lost} onChange={(e) => setStatusForm((f) => ({ ...f, is_lost: e.target.checked }))} className="rounded border-slate-300 text-red-600 w-3.5 h-3.5" /><span className="text-red-600">Lost</span></label>
                                  <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.is_hot} onChange={(e) => setStatusForm((f) => ({ ...f, is_hot: e.target.checked }))} className="rounded border-slate-300 text-amber-500 w-3.5 h-3.5" /><span className="text-amber-600">Hot</span></label>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-500">
                                  <Tooltip content="Auto-set when quotation added"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.set_when_quotation_added} onChange={(e) => setStatusForm((f) => ({ ...f, set_when_quotation_added: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Quote+</span></label></Tooltip>
                                  <Tooltip content="Auto-set when lead # generated"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.set_when_quote_number_generated} onChange={(e) => setStatusForm((f) => ({ ...f, set_when_quote_number_generated: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Lead#</span></label></Tooltip>
                                  <Tooltip content="Require attachment on Kanban move"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={statusForm.attachment_required_on_kanban_change} onChange={(e) => setStatusForm((f) => ({ ...f, attachment_required_on_kanban_change: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" /><span>Attach req.</span></label></Tooltip>
                                </div>
                                <div className="flex gap-1 ml-auto">
                                  <Button size="xs" onClick={saveStatus} disabled={savingStatus || !statusForm.label?.trim()}>{savingStatus ? '...' : 'Save'}</Button>
                                  <Button size="xs" variant="outline" onClick={() => { setEditingStatus(null); setStatusForm({ code: '', label: '', display_order: 0, group_id: undefined, is_active: true, is_final: false, is_lost: false, hex_color: '', set_when_quotation_added: false, set_when_quote_number_generated: false, attachment_required_on_kanban_change: false, is_hot: false }); }}>Cancel</Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50/60 transition-colors group/row">
                              {/* Color dot + label */}
                              <div className="flex items-center gap-2 min-w-[140px]">
                                <span className="w-3 h-3 rounded-full shrink-0 border border-black/10" style={{ backgroundColor: s.hex_color || '#94a3b8' }} />
                                <span className="text-sm font-medium text-slate-800 truncate">{s.label}</span>
                              </div>
                              {/* Code badge */}
                              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{s.code}</span>
                              {/* Order */}
                              <span className="text-[10px] text-slate-400 tabular-nums shrink-0">#{s.display_order}</span>
                              {/* Flag badges */}
                              <div className="flex items-center gap-1 flex-wrap flex-1">
                                {!s.is_active && <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Inactive</span>}
                                {s.is_final && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Won</span>}
                                {s.is_lost && <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Lost</span>}
                                {s.is_hot && <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Hot</span>}
                                {s.set_when_quotation_added && <Tooltip content="Auto-set when quotation added"><span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded cursor-help">Quote+</span></Tooltip>}
                                {s.set_when_quote_number_generated && <Tooltip content="Auto-set when lead # generated"><span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded cursor-help">Lead#</span></Tooltip>}
                                {s.attachment_required_on_kanban_change && <Tooltip content="Require attachment on Kanban move"><span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded cursor-help">Attach req.</span></Tooltip>}
                              </div>
                              {/* Actions */}
                              <div className="flex gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0">
                                {canEdit && <Button variant="ghost" size="xs" className="h-6 text-xs" onClick={() => openEditStatus(s)}>Edit</Button>}
                                {canDelete && <Button variant="ghost" size="xs" className="h-6 text-xs text-rose-600" onClick={() => setDeleteStatusId(s.id)}>Delete</Button>}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showLeadTypeModal}
        onClose={() => {
          setShowLeadTypeModal(false);
          setEditingLeadType(null);
          setLeadTypeForm({ code: '', label: '', display_order: 0, is_active: true });
        }}
        title="Lead types (Lead for)"
      >
        <div className="space-y-3">
          {/* Inline add form */}
          {canEdit && !editingLeadType && (
            <div className="border border-dashed border-blue-200 rounded-lg px-3 py-2.5 bg-blue-50/40">
              <div className="flex items-center gap-3 flex-wrap">
                <input className="h-7 w-full max-w-[8rem] rounded border border-slate-200 bg-white px-2 text-xs font-mono focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="code" value={leadTypeForm.code} onChange={(e) => setLeadTypeForm((f) => ({ ...f, code: e.target.value }))} />
                <input className="h-7 w-full max-w-[12rem] rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Type name (e.g. Walk-in)" value={leadTypeForm.label} onChange={(e) => setLeadTypeForm((f) => ({ ...f, label: e.target.value }))} />
                <input type="number" className="h-7 w-12 rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" title="Order" value={leadTypeForm.display_order} onChange={(e) => setLeadTypeForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={leadTypeForm.is_active} onChange={(e) => setLeadTypeForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" />
                  Active
                </label>
                <Button size="xs" className="ml-auto" onClick={saveLeadType} disabled={savingLeadType || !leadTypeForm.label?.trim()} leftIcon={<Plus size={12} />}>
                  {savingLeadType ? '...' : 'Add'}
                </Button>
              </div>
            </div>
          )}

          {/* Type rows */}
          {leadTypes.length === 0 ? (
            <div className="text-center py-6 text-xs text-slate-400 italic">No lead types yet. Add one above.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
              {leadTypes.map((t) =>
                canEdit && editingLeadType?.id === t.id ? (
                  <div key={t.id} className="px-3 py-2.5 bg-amber-50/50 border-l-2 border-amber-300">
                    <div className="flex items-center gap-3 flex-wrap">
                      <input className="h-7 w-full max-w-[8rem] rounded border border-slate-200 bg-white px-2 text-xs font-mono focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="code" value={leadTypeForm.code} onChange={(e) => setLeadTypeForm((f) => ({ ...f, code: e.target.value }))} />
                      <input className="h-7 w-full max-w-[12rem] rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" placeholder="Label" value={leadTypeForm.label} onChange={(e) => setLeadTypeForm((f) => ({ ...f, label: e.target.value }))} />
                      <input type="number" className="h-7 w-12 rounded border border-slate-200 bg-white px-2 text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200" title="Order" value={leadTypeForm.display_order} onChange={(e) => setLeadTypeForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))} />
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={leadTypeForm.is_active} onChange={(e) => setLeadTypeForm((f) => ({ ...f, is_active: e.target.checked }))} className="rounded border-slate-300 text-blue-600 w-3.5 h-3.5" />
                        Active
                      </label>
                      <div className="flex gap-1 ml-auto">
                        <Button size="xs" onClick={saveLeadType} disabled={savingLeadType || !leadTypeForm.label?.trim()}>{savingLeadType ? '...' : 'Save'}</Button>
                        <Button size="xs" variant="outline" onClick={() => { setEditingLeadType(null); setLeadTypeForm({ code: '', label: '', display_order: leadTypes.length, is_active: true }); }}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50/60 transition-colors group/row">
                    <span className="text-sm font-medium text-slate-800 min-w-[140px] truncate">{t.label}</span>
                    <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">{t.code}</span>
                    <span className="text-[10px] text-slate-400 tabular-nums shrink-0">#{t.display_order}</span>
                    {!t.is_active && <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Inactive</span>}
                    <div className="flex-1" />
                    <div className="flex gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity shrink-0">
                      {canEdit && <Button variant="ghost" size="xs" className="h-6 text-xs" onClick={() => openEditLeadType(t)}>Edit</Button>}
                      {canDelete && <Button variant="ghost" size="xs" className="h-6 text-xs text-rose-600" onClick={() => setDeleteLeadTypeId(t.id)}>Delete</Button>}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        isOpen={deleteStatusId != null}
        onClose={() => setDeleteStatusId(null)}
        onConfirm={confirmDeleteStatus}
        title="Delete lead status"
        message="Are you sure? Leads using this status must be reassigned first."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteGroupId != null}
        onClose={() => setDeleteGroupId(null)}
        onConfirm={confirmDeleteGroup}
        title="Delete status group"
        message="Are you sure? Statuses in this group will be ungrouped."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteLeadTypeId != null}
        onClose={() => setDeleteLeadTypeId(null)}
        onConfirm={confirmDeleteLeadType}
        title="Delete lead type"
        message="Are you sure? Leads using this type must be reassigned first."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
    </PageLayout>
  );
};
